// CatalogView — the Catalog screen, extracted from App.jsx (2026-07-20) so it
// is a peer of ProductsView: App owns navigation state (selectedNodeId,
// deep-links, history) and passes it down; this file owns the catalog data,
// handlers, columns and the workspace render. The extraction is what lets the
// Onshape screen embed the REAL catalog Main for a selected node (UI_VISION
// §8.0's "whichever grid matches the selected node") instead of a copy.
//
// Props:
//   selectedNodeId / onSelectNode — App-owned nav state (same contract as
//     ProductsView). onSelectNode receives NORMALIZED category ids — leaf ids
//     (c3-N|lineId / c3-N|fam-M) are resolved internally to the L3 + a
//     family/line selection, exactly as before the extraction.
//   initialFamilyId — deep-link seed (`?node=c3-N|fam-M`), read once.
//   activeProjectId — the banner's app-level project context (usage columns).
//   catalogTarget — cross-screen navigation target { l1, l2, l3, token }
//     (codes, not ids — replaces App reaching into this screen's tree).
//   onNavigateToProduct / onNavigateToPlacement — cross-screen jumps (App owns
//     view switching).

import { useState, useEffect, useCallback, useRef, useMemo, useContext } from 'react';
import MoneyCell from './MoneyCell.jsx';
import Text from './components/Text.jsx';
import { formatQty } from './formatMoney.js';
import { rate, money } from './format.js';
import { formulaCol, productivityCol } from './format-cols.jsx';
import { WARN, OK, EDIT, VALIDATED_SYNCED, DEPRECATED, DRAFT, IN_PROGRESS, LINK, COPY } from './icons.js';
import { StatusCounts } from './components/TreeNav.jsx';
import AssistantSection from './components/AssistantSection.jsx';
import DataGrid from './components/DataGrid.jsx';
import StatusCell from './components/StatusCell.jsx';
import SelectCell from './components/SelectCell.jsx';
import ExplainerSubLine from './components/ExplainerSubLine.jsx';
import HierarchyWorkspace from './components/HierarchyWorkspace.jsx';
import ViewSwitcher from './components/ViewSwitcher.jsx';
import { OnshapeRefCell } from './product-cols.jsx';
import { makeFolderCols } from './folder-cols.jsx';
import ImageCell from './components/ImageCell.jsx';
import OnshapeSection from './components/OnshapeSection.jsx';
import CascadeSelect, { CascadeDropdown, useCascadeSignal } from './components/CascadeSelect.jsx';
import Notice from './components/Notice.jsx';
import AssistantWriteNotice, { useAssistantWrite } from './components/AssistantWriteNotice.jsx';
import ConfirmPanel from './components/ConfirmPanel.jsx';
import Breadcrumb from './components/Breadcrumb.jsx';
import { composeTree, ownOnlyTree } from './tree-model.js';
import ShowStandardCheck, { nothingOwnMessage } from './components/ShowStandardCheck.jsx';
import { WorkingInContext } from './can-change.js';
import { onshapeBaseConfiguredUrl } from '../onshape-ref.js';
import HeaderRow from './components/HeaderRow.jsx';
import Card from './components/Card.jsx';
import CategoryOnshapePanel from './components/CategoryOnshapePanel.jsx';
import NotesSection from './components/NotesSection.jsx';
import OwnerLabel from './components/OwnerLabel.jsx';
import { useAuthGroup } from './AuthGate.jsx';
import { useCanChange } from './can-change.js';

import { NAVY, PANE_GAP, BTN_TAB_RADIUS, BTN_PRIMARY, BTN_SECONDARY, BTN_DISABLED, BTN_GLYPH } from './theme-tokens.js';
import { USED_IN_PRODUCTS_COLS } from './usage-cols.jsx';
import CatalogHistoryCard from './catalog-history.jsx';
const STATUSES = ['draft', 'in_progress', 'validated_db', 'validated_synced', 'deprecated'];

// Default status filter — everything EXCEPT deprecated. Also the RESET target
// when the grid changes (Alex, 2026-07-18): the filter is per-grid, not global.
// A filter left on from a previous L3 silently emptied the next one — the tree
// is NOT status-filtered but the grid IS, so a validated_db family showed a
// tree node that rendered zero rows. That mismatch read as the tree behaving
// randomly (it hit ~44% of the catalog — every validated_db line).
const DEFAULT_STATUS_FILTER = STATUSES.filter(s => s !== 'deprecated');

const STATUS_LABEL = {
  draft: 'Draft',
  in_progress: 'In Progress',
  validated_db: 'Validated DB',
  validated_synced: 'Validated Synced',
  deprecated: 'Deprecated',
};

const STATUS_COLOR = {
  draft: 'var(--th-status-draft)',
  in_progress: 'var(--th-status-inprogress)',
  validated_db: NAVY,
  validated_synced: 'var(--th-status-synced)',
  deprecated: 'var(--th-status-deprecated)',
};

// Same glyphs as components/StatusCell.jsx — color-blind safe (icon + word,
// never color alone); reused here for the catalog status-filter chips.
const STATUS_ICON = {
  draft: DRAFT,
  in_progress: IN_PROGRESS,
  validated_db: OK,
  validated_synced: VALIDATED_SYNCED,
  deprecated: DEPRECATED,
};

export { STATUSES, STATUS_LABEL, STATUS_COLOR };

// CostClass column applicability (Alex, 2026-07-03) — which catalog-grid columns
// the cost ENGINE actually reads, per cost_class, verified against product-db.js
// (laborAddon) rather than assumed. Columns not listed here (Tag/Description/Size/
// Status/Onshape/etc.) are engine-agnostic and always apply. Keyed on cost_class
// (rename-immune), never the L1 code literal.
//
// ⚠ MOST OF THIS MAP RETIRED ON 2026-08-18 with catalog_fabrication. The six
// fabrication columns it gated — QtyDrv, FabRate, Labour, RM Source, Unit Conv,
// Net→Gross — are gone from the grid, because a fabricated item is a PRODUCT now:
// its material and labour are components with their own quantities, priced on the
// Product screen (PATTERN_fabrication_primitive.md). A catalog LINE no longer
// carries a fabrication recipe, so there is no longer a class for which those
// columns are #NA — there are no such columns.
//
// What is left is the small, true remainder:
//   MF  — hrs and labor_productivity are not read on a fabricated shelf.
//   HR  — hrs (line_hrs) is never read: laborAddon returns {lh:eff, lc:0} before
//         it looks at line_hrs, because for HR the QUANTITY is the hours and the
//         price is $/hr. Price very much applies.
//   RM/PE/SC/EX — general laborAddon path: price + hrs both apply.
//         labor_productivity is HR-only (NIGHT_SHIFT.md item 5, Alex's call: the
//         LABOUR LINE carries the crew's pace), so it is #NA everywhere else.
//   unclassed (cost_class null/unresolved) — no #NA anywhere; don't hide data on
//         an unknown class.
//   Material is NOT #NA on any class (2026-07-30). `catalog_families.material_id`
//         records WHAT the item is made of — a DESIGN fact, not a cost input.
//         #NA'ing it made it unsettable on 15 families that already carried one.
//         Alex: "I cannot change the material (the main one, not the one for the
//         cost). It should be an editable drop down."
//
// NOT gated, deliberately:
//   kg (Mass)   — 351 RM rows carry it but NO engine read was found; it reads as
//                 descriptive. #NA would BLOCK entering a weight on a purchased
//                 item, which is worse than showing a field nothing computes.
//   is_material / is_labor — already handled better than #NA: those columns are
//                 only RENDERED for RM / HR respectively (see the cols memo).
//   size/unit/label/status/onshape_ref — universal, every class uses them.

const NA_COLUMNS_BY_CLASS = {
  // 'price' is NOT #NA on MF any more (Alex, 2026-07-27): a fabricated line HAS
  // a unit price, it is just computed (RM + fab hours) rather than typed. The
  // Price column renders it derived-grey and read-only -- see fabPriceCol below.
  MF: ['hrs', 'labor_productivity'],
  RM: ['labor_productivity'],
  HR: ['hrs'],
  PE: ['labor_productivity'],
  SC: ['labor_productivity'],
  EX: ['labor_productivity'],
};

// Wrap a column def so it renders a muted, non-editable "#NA" for classes where
// the engine never reads that field — instead of the normal editable cell. Only
// applied when curL1Class resolves AND is in the col's #NA set; unclassed/other
// classes fall through to the column unchanged (col.render/editable untouched).
// `col.naKey` overrides which key the #NA set is matched against, for a column
// whose edit/stage key differs from the field the ENGINE cares about (Price,
// since NIGHT_SHIFT item 4 — its col.key is now 'price_expr', a formula
// staging field, but NA_COLUMNS_BY_CLASS still means "does the engine read
// the flat price?", so the gate must still match on 'price').
function naCol(col, curL1Class) {
  if (!curL1Class) return col;
  const naSet = NA_COLUMNS_BY_CLASS[curL1Class];
  if (!naSet || !naSet.includes(col.naKey || col.key)) return col;
  return {
    ...col,
    editable: false,
    editType: undefined,
    render: () => <Text kind="empty">#NA</Text>,
  };
}

// --- UX-40 (Alex, 2026-08-21) — family rows, sizes in their own card --------
// "445 families, 1,156 lines — 349 families (78%) have ONE line." The L3 grid
// below groups its lines by family_id: a family with exactly one line passes
// through as an ordinary line row (same shape gridRows always built — every
// existing editor keeps working, nothing here applies to it); a family with
// more than one becomes a read-only SUMMARY row (this section), and editing
// moves to the Sizes card. Only three columns render something wrong or
// misleading for a summary row out of the box (a stray single size, a single
// price plucked from a ladder, a size-specific Onshape link) — label, unit,
// material and status are already uniform across a family's sizes, so the
// plain value is correct as-is and needs no wrapper.

// numRange — min/max across a family's sizes for one numeric field.
// `uniform` says whether every size actually agrees, so a family that
// happens to have one price across N sizes still renders as a single value
// instead of "$9.60 – $9.60".
function numRange(vals) {
  const nums = vals.filter(v => v != null && v !== '').map(Number).filter(v => !Number.isNaN(v));
  if (!nums.length) return { has: false };
  const min = Math.min(...nums), max = Math.max(...nums);
  return { has: true, min, max, uniform: min === max };
}

// Same aggregation rule the tree already uses for a family leaf's own label
// (catalog-db.js listCatalogTree, B2) — kept in agreement on purpose, so the
// grid row and its tree node never disagree about one family's status.
function familyAggStatus(famLines) {
  if (famLines.every(l => l.status === 'deprecated')) return 'deprecated';
  if (famLines.some(l => l.status === 'draft' || l.status === 'in_progress')) return 'in_progress';
  return famLines[0].status;
}

// Size column: "N sizes" instead of one size (there is no single one), AND the
// control that opens the Sizes card.
//
// ⚠ IT USED TO SAY "this is a label, not a second click target", and the row's
// double-click was supposed to open the card. That has been DEAD since 2026-08-23
// (e72754a), which in one pass both moved the gesture to double-click and made a
// single click enter edit mode. With editOnRowClick the first click sets
// editRowId, so by the time the second arrives DataGrid's handler hits
// `if (isRowInEditMode) return` and never drills. Each half was reasonable; only
// together do they cancel out, which is why nobody noticed. Alex, 2026-08-26:
// "I cannot see all sizes in a size card... But it was before."
//
// So the cell becomes the click target it already LOOKED like — navy and bold is
// what every clickable thing in this app wears, and promising a click that does
// nothing is the part that made this hard to find. A family-summary row has no
// single size to edit, so this steals nothing from click-to-edit: it gives a
// non-editable cell the one job it was already advertising.
function aggSizeCol(col, onOpenSizes) {
  return {
    ...col,
    render: (row, ctx) => row._type === 'familyAgg'
      ? (
        <span
          role="button"
          tabIndex={0}
          title={`Show the ${row.sizeCount} sizes in this family`}
          // stopPropagation, or the click also reaches the row and opens the
          // editor underneath the card it just opened.
          onClick={(e) => { e.stopPropagation(); onOpenSizes?.(row); }}
          onKeyDown={(e) => {
            if (e.key !== 'Enter' && e.key !== ' ') return;
            e.preventDefault(); e.stopPropagation(); onOpenSizes?.(row);
          }}
          style={{
            fontSize: 'var(--fs-small)', color: NAVY, fontWeight: 600,
            cursor: 'pointer', textDecoration: 'underline', textUnderlineOffset: 2,
          }}
        >
          {row.sizeCount} sizes
        </span>
      )
      : col.render(row, ctx),
  };
}

// Unit Cost: BACKLOG UX-40(a) — "one line ⇒ show its price; many ⇒ show the
// RANGE ($9.60 – $17.20). A range is informative, needs no colour, and says
// 'this is a ladder' without a second signal." Same MoneyCell the single-value
// case already renders with (formulaCol, kind:'money') — only the shape changes.
function aggPriceCol(col) {
  return {
    ...col,
    render: (row, ctx) => {
      if (row._type !== 'familyAgg') return col.render(row, ctx);
      const r = row._priceRange;
      if (!r || !r.has) return <Text kind="empty" />;
      if (r.uniform) return <MoneyCell n={r.min} unit />;
      return (
        <span style={{ display: 'inline-flex', alignItems: 'baseline', gap: 4, fontSize: 'var(--fs-small)' }}>
          <MoneyCell n={r.min} unit /><span style={{ color: 'var(--th-text-secondary)' }}>–</span><MoneyCell n={r.max} unit />
        </span>
      );
    },
  };
}

// Onshape: the per-line render picks a SIZE's own configured URL
// (row._onshape_url), which a summary row has none of — showing it unchanged
// would read "Size doesn't map to a valid Onshape option id" on a family that
// is perfectly linked. Link to the family's base config instead (same
// derivation FamilyOnshapePanel's own href uses).
//
// A SINGLE CLICK ON THIS CELL SHOWS THE FAMILY'S 3D MODEL CARD (Alex, 2026-09-30:
// "Can we try with single click on the 3D cell?"). Same reasoning as the "N sizes"
// cell above: the row's own click is edit, so the cell that IS about the model
// takes the "show me this family's model" job. Every row with a family gets it —
// a one-size family too, which double-click never reached (it selects the size).
// The ↗ inside keeps its own job, opening Onshape; it stops its click first.
function aggOnshapeCol(col, _onOpenSizes, onOpenModel) {
  const inner = (row, ctx) => {
    if (row._type !== 'familyAgg') return col.render(row, ctx);
    if (!row.family_onshape_ref) return <OnshapeRefCell value={null} />;
    const href = onshapeBaseConfiguredUrl(row.family_onshape_ref, null);
    return (
      <a
        href={href} target="_blank" rel="noopener noreferrer"
        title="Open in Onshape" aria-label="Open in Onshape"
        onClick={e => e.stopPropagation()}
        style={{ display: 'inline-flex', alignItems: 'center', color: NAVY, fontSize: 'var(--fs-small)' }}
      >{LINK}</a>
    );
  };
  return {
    ...col,
    render: (row, ctx) => (row.family_id == null || !onOpenModel ? inner(row, ctx) : (
      <span
        role="button"
        tabIndex={0}
        title="Show this family's 3D model below"
        // stopPropagation, or the click also reaches the row and opens the editor.
        onClick={(e) => { e.stopPropagation(); onOpenModel(row); }}
        onKeyDown={(e) => {
          if (e.key !== 'Enter' && e.key !== ' ') return;
          e.preventDefault(); e.stopPropagation(); onOpenModel(row);
        }}
        style={{ display: 'flex', alignItems: 'center', minHeight: 18, cursor: 'pointer' }}
      >{inner(row, ctx)}</span>
    )),
  };
}

// Cleanup batch (Alex 2026-07-03): compact status-filter chip row, moved from the
// top banner down to the Catalog screen's own header (near the breadcrumb). Pressed
// state is border/weight + icon, never color alone (color-blind safe).
function CatalogStatusFilter({ statusFilter, onToggle }) {
  return (
    <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
      {STATUSES.map(s => {
        const active = statusFilter.includes(s);
        return (
          <button
            key={s}
            onClick={() => onToggle(s)}
            title={STATUS_LABEL[s]}
            aria-pressed={active}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 4,
              background: active ? 'var(--tg-chip-bg)' : 'transparent',
              color: 'var(--th-text-body)',
              border: active ? '1.5px solid var(--th-navy)' : '1px solid var(--th-border-subtle)',
              // Rectangular with a soft corner, matching Copy shelf beside it --
              // Alex, 2026-08-03: "like the other ones ... instead of oblong". Was
              // 10, which read as a pill at this height. BTN_TAB_RADIUS, not its own
              // number, so this chip row and ViewSwitcher's tabs share one radius.
              padding: '2px 8px', borderRadius: BTN_TAB_RADIUS, cursor: 'pointer',
              fontSize: 'var(--fs-small)', fontWeight: active ? 700 : 400,
            }}
          >
            <span aria-hidden="true" style={{ fontFamily: 'var(--ff-mono)' }}>{STATUS_ICON[s]}</span>
            <span>{STATUS_LABEL[s]}</span>
          </button>
        );
      })}
    </div>
  );
}

// Family-level Onshape linking (2026-07-03) — compact edit affordance for a
// selected family's onshape_ref + onshape_config. Same card-panel idiom
// as the product page's Onshape panel (ProductsView.jsx): border/background,
// small-text labels, plain inputs, Save/Clear buttons. Pricing never calls
// Onshape — this is pure stored text (see ONSHAPE_LINKING.md).
function FamilyOnshapePanel({ familyId, onshapeRef, onshapeConfig, onshapeSize, onSaved, costClass = null }) {
  const [refDraft, setRefDraft] = useState(onshapeRef || '');
  const [baseDraft, setBaseDraft] = useState(onshapeConfig || '');
  // Generalized size axis (2026-07-04): the per-size config PARAM NAME (e.g.
  // 'Type'). Empty = unchanged NPS-derived 'Size=NPSxx' behavior.
  const [sizeParamDraft, setSizeParamDraft] = useState(onshapeSize || '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    setRefDraft(onshapeRef || '');
    setBaseDraft(onshapeConfig || '');
    setSizeParamDraft(onshapeSize || '');
    setError(null);
  }, [familyId, onshapeRef, onshapeConfig, onshapeSize]);

  const dirty = refDraft !== (onshapeRef || '') || baseDraft !== (onshapeConfig || '') || sizeParamDraft !== (onshapeSize || '');

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      const res = await fetch(`/api/catalog/families/${familyId}`, {
        method: 'PATCH', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ onshape_ref: refDraft.trim(), onshape_config: baseDraft.trim(), onshape_size: sizeParamDraft.trim() }),
      });
      if (!res.ok) { const e = await res.json(); throw new Error(e.error || 'Save failed'); }
      await onSaved?.();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  // The card frame, link row, thumbnail and View 3D all come from the SHARED
  // OnshapeSection (components/OnshapeSection.jsx, UI_VISION §8 step 1); this
  // panel contributes only the family-specific link editor as its children.
  // Bug 1 (2026-07-21): the family-level "Open in Onshape" link never carried
  // the family's own configuration — it opened Onshape's default config even
  // when this family is pinned to e.g. Type=X. Base-only (no size — a family
  // has no single size to pin) is exactly right at family level: Onshape
  // applies its own defaults for whatever's left unspecified. Null (no
  // onshape_config) falls back to the bare doc url inside OnshapeSection.
  const familyOnshapeHref = onshapeRef ? onshapeBaseConfiguredUrl(onshapeRef, onshapeConfig) : null;

  return (
    <OnshapeSection
      onshapeRef={onshapeRef || null}
      onshapeHref={familyOnshapeHref}
      storageKey="catalog-family"
      // The family's picture beside the 3D MODEL title (Alex, 2026-08-27).
      // Unlike the product's, this section draws its OWN title, so the icon
      // goes on the section rather than on a spot.
      dbId={familyId != null ? `fam:${familyId}` : null}
      // Labour can never be modelled in CAD (STATUS: architecture, not
      // preference), so under HR the section is permanently #NA rather than
      // "not linked yet" — UI_VISION §4's two collapse reasons. cost_class is
      // the rename-immune classifier; the L1 CODES were all renamed once and
      // the code-keyed version silently broke.
      applicable={costClass !== 'HR'}
      naReason="Labour has no CAD model — Onshape never applies here."
    >
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 2, fontSize: 'var(--fs-small)', color: 'var(--th-text-secondary)' }}>
          Onshape ref (did/wid/eid)
          <input
            value={refDraft}
            onChange={e => setRefDraft(e.target.value)}
            placeholder="did/wid/eid"
            style={{ fontSize: 'var(--fs-small)', padding: '3px 6px', border: '1px solid var(--th-border-subtle)', borderRadius: 3, width: 340, fontFamily: 'var(--ff-mono)' }}
          />
        </label>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 2, fontSize: 'var(--fs-small)', color: 'var(--th-text-secondary)' }}>
          Config base (non-size axes)
          <input
            value={baseDraft}
            onChange={e => setBaseDraft(e.target.value)}
            placeholder="e.g. Drive=GO;Hand=true"
            style={{ fontSize: 'var(--fs-small)', padding: '3px 6px', border: '1px solid var(--th-border-subtle)', borderRadius: 3, width: 220, fontFamily: 'var(--ff-mono)' }}
          />
        </label>
        <label
          style={{ display: 'flex', flexDirection: 'column', gap: 2, fontSize: 'var(--fs-small)', color: 'var(--th-text-secondary)' }}
          title="Per-size config param name (e.g. 'Type'); option value = the line's own Size TEXT verbatim. Blank = default NPS-based Size=NPSxx."
        >
          Size param (blank = NPS)
          <input
            value={sizeParamDraft}
            onChange={e => setSizeParamDraft(e.target.value)}
            placeholder="e.g. Type"
            style={{ fontSize: 'var(--fs-small)', padding: '3px 6px', border: '1px solid var(--th-border-subtle)', borderRadius: 3, width: 120, fontFamily: 'var(--ff-mono)' }}
          />
        </label>
        <button
          onClick={save}
          disabled={saving || !dirty}
          style={{ ...BTN_PRIMARY, ...((saving || !dirty) ? BTN_DISABLED : null) }}
        >{saving ? 'Saving…' : 'Save'}</button>
        {/* COS-1(b): every grid row already gets Save+Cancel — this editor was
            the outlier, offering only Save with no way back to the stored values. */}
        {dirty && !saving && (
          <button
            onClick={() => { setRefDraft(onshapeRef || ''); setBaseDraft(onshapeConfig || ''); setSizeParamDraft(onshapeSize || ''); setError(null); }}
            style={BTN_SECONDARY}
          >Cancel</button>
        )}
      </div>
      {error && (
        <Text kind="deprecated" as="div" style={{ marginTop: 6 }}>{WARN} {error}</Text>
      )}
    </OnshapeSection>
  );
}

// Catalog 1b — node id helpers for the unified catalog_categories tree.
// Exported: App.jsx's deep-link init normalizes a family leaf id before it
// ever reaches this component.
//   c1-<id> / c2-<id> / c3-<id>  → category nodes
//   c3-<catId>|<lineId>          → data-line leaf (standalone, no family)
//   c3-<catId>|fam-<famId>       → family leaf (B2) — represents ALL sizes of one item
export const isCatNodeId = (id) => typeof id === 'string' && /^c[123]-\d+$/.test(id);
export const isLineNodeId = (id) => typeof id === 'string' && /^c3-\d+\|\d+$/.test(id);
export const isFamilyNodeId = (id) => typeof id === 'string' && /^c3-\d+\|fam-\d+$/.test(id);
// Either leaf form under an L3 — used wherever the code needs "this is a leaf,
// not a category" regardless of standalone-line vs family grouping.
export const isAnyLineLeafId = (id) => isLineNodeId(id) || isFamilyNodeId(id);
// The L3 category node id a leaf (either form) belongs to.
export const catNodeIdOfLeaf = (id) => `c3-${id.split('|')[0].slice(3)}`;

// Overnight batch D — recursively sum a category subtree's project-usage.
// L3 nodes carry a flat `lines` array (every catalog line under them, both
// sized and non-sized); byLine already carries BOTH plain-ref and sized-ref
// (via resolved_line_id) contributions keyed on the literal catalog line id,
// so summing byLine over every line in the subtree covers both cases — no
// separate byFamily walk needed here. Non-L3 nodes recurse into `children`.
function sumCatalogUsageForCategory(node, catalogUsage) {
  if (!node || !catalogUsage) return { qty: 0, cost: 0 };
  let qty = 0, cost = 0;
  if (node.lines) {
    for (const ln of node.lines) {
      const u = catalogUsage.byLine[String(ln._lineId ?? ln.id)];
      if (u) { qty += u.qty; cost += u.cost; }
    }
  }
  if (node.children) {
    for (const child of node.children) {
      if (child.isLine) continue; // leaf display nodes — already covered by node.lines
      const sub = sumCatalogUsageForCategory(child, catalogUsage);
      qty += sub.qty; cost += sub.cost;
    }
  }
  return { qty: Math.round(qty * 10000) / 10000, cost: Math.round(cost * 100) / 100 };
}

// Read-only render for the is_material/is_labor Flag column (Alex, 2026-07-02).
// Color-blind safe: icon + word, never color alone. Edit-mode input is CheckboxCell
// (routed by DataGrid via editType:'checkbox'); this is only the static display.
function CheckboxCellRO({ value }) {
  const on = value === 1 || value === '1' || value === true;
  return (
    <span style={{ fontSize: 'var(--fs-small)', color: on ? 'var(--th-navy)' : 'var(--th-text-disabled)' }}>
      {on ? <>{OK} Yes</> : '— No'}
    </span>
  );
}

// Catalog #2 — same-L1 re-parent dropdowns, now on the shared CascadeSelect
// (Cascade consistency, 2026-07-08) — mirrors ProductsView's PathDropdowns /
// ProjectsView's "Place into…" cascade: two-column code|label popovers.
// The catalog L1 IS the cost classification (RM/HR/PE/MF/SC/FR); per the A75
// model cross-L1 reparent is disallowed, so L1 renders LOCKED (disabled, shows
// the row's own L1 code) and only L2 (variant 'l3') or L2+L3 (variant 'line')
// are editable, always scoped to that same L1 — no cross-L1 in this UI.
//   variant 'l3'   — an L3 category row → pick a new L2 parent under its own L1.
//   variant 'line' — a data line → pick L2 then a new L3 parent under its own L1.
// disabled (row not in edit mode) → render the current path as plain text, no dropdowns.
// l1Id/l2Id seed the current ancestors so the locked L1 + pre-filled L2 read correctly.
// onMove* receives the chosen target id AND the (now-always-current) L1 node —
// signature unchanged so handleReparentCategory/handleReparentLine (incl. the
// crossL1Ok confirm, now always a no-op since L1 can't change here) keep working.
// onNavigate (breadcrumb retirement, 2026-07-20): in READ mode the path renders
// as clickable crumbs that navigate up the hierarchy — the breadcrumb's job
// absorbed by the Parent cell (§5: "the breadcrumb and the move cascade are the
// same information — one read-only, one editable"). currentL3 ({id, code},
// optional) appends the row's own L3 as a third crumb so a header row shows the
// FULL path. Omit onNavigate and read mode stays plain text.
// SizeEditCell — the catalog Size cell's two controls (unit, then value).
//
// Extracted 2026-08-04 for ONE reason: this cascade had no auto-advance, and the
// signal needs a hook, which cannot be called from a column's render(row, ctx).
// Picking "NPS" left the size dropdown closed, so setting a size took two clicks
// where every other cascade takes one -- against Alex's rule that picking a level
// MUST open the next. Nothing else about the cell changed: the same staging through
// ctx.buffer (a cascade in a grid never writes on pick), the same custom-value input
// with its datalist, the same "#NA -- no size" clearing of BOTH halves.
// A TYPED size (Alex, 2026-10-12, "Go" on: "you extend the list by typing a size that
// isn't there yet"). The last option of the Size picker, "+ new size…", turns the picker
// into a box; so does a row created on a FULL list, which arrives here with
// `startTyping` and the guessed next size already in the box (Std RB at 9 → "10").
// Saving stages `new_size_look`, which the server resolves: a label already on the
// list is simply picked, a new one is added to the list first. The words beside the
// Save says which of the two will happen (sizeEditWarnings) — that line is the
// confirmation, and Save is the yes.
const NEW_SIZE = '__new_size__';
function SizeEditCell({
  lockedUnit, unitValue, unitOptions, naUnit, effectiveUnit,
  sizeIdValue, sizeOptions, customValue, customSuggestions, suggestListId,
  stage, fetchSizeListValues, startTyping, guess, typedValue,
}) {
  const cascade = useCascadeSignal();
  const [typing, setTyping] = useState(!!startTyping);
  useEffect(() => {
    if (startTyping && guess) stage({ new_size_look: guess });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const canType = !!effectiveUnit && effectiveUnit !== 'custom' && effectiveUnit !== 'unique' && effectiveUnit !== naUnit;

  // ⚠ THE SIZE PICKER WAS UNCLICKABLE on a sized family (Alex, 2026-10-11: "I
  // cannot change to 8, 9, or 10"). The column is 80px; the locked unit button
  // alone is wider, so the size button was pushed past the column edge, where the
  // next cell (Unit Cost's input) is painted OVER it — the "— ▾" was visible and
  // every click on it landed in Unit Cost. So: a LOCKED unit is not drawn (it is
  // the family's, cannot be changed here, and the column header already says
  // Size), and the cell sits above its neighbour so the unlocked two-control case
  // — which still overflows while you pick a unit — stays clickable too.
  return (
    <div style={{ display: 'flex', gap: 4, alignItems: 'center', position: 'relative', zIndex: 1 }}>
      {!lockedUnit && <CascadeDropdown
        ariaLabel="Size Unit"
        width={90}
        allowClear={false}
        placeholder="— pick —"
        disabled={!!lockedUnit}
        value={unitValue}
        options={unitOptions}
        onChange={unit => {
          if (lockedUnit) return;
          if (unit && unit !== 'custom' && unit !== naUnit) fetchSizeListValues(unit);
          // "#NA — no size" clears BOTH halves: no list, and no value on
          // either the standard (size_id) or custom (size) side. Clearing
          // only size_list would leave a stale size behind it. It stores
          // '' — the sentinel is a UI value only, never a stored one.
          if (!unit || unit === naUnit) stage({ size_list: '', size_id: '', size: '' });
          else {
            stage({ size_list: unit });
            // ...and open the value picker. Not for 'custom' (a free-text box, which
            // gets focus of its own accord) and not for #NA (there is no value to pick).
            if (unit !== 'custom') cascade.advance('size');
          }
        }}
      />}
      {effectiveUnit === 'custom' ? (
        <>
          <input
            list={suggestListId}
            defaultValue={customValue}
            onBlur={e => {
              const v = e.target.value;
              if (v !== customValue) stage({ size_list: 'custom', size: v });
            }}
            style={{ width: 150, fontSize: 'var(--fs-small)', padding: '2px 5px', border: '1px solid var(--th-border-subtle)', borderRadius: 3 }}
          />
          <datalist id={suggestListId}>
            {customSuggestions.map(s => <option key={s} value={s} />)}
          </datalist>
        </>
      ) : typing && canType ? (
        <>
          <input
            aria-label="New size"
            autoFocus
            defaultValue={typedValue ?? ''}
            placeholder="size"
            onChange={e => stage({ new_size_look: e.target.value })}
            style={{ width: 56, fontSize: 'var(--fs-small)', padding: '2px 5px', border: '1px solid var(--th-border-subtle)', borderRadius: 3 }}
          />
        </>
      ) : effectiveUnit ? (
        <CascadeDropdown
          ariaLabel="Size"
          width={150}
          allowClear={false}
          placeholder="— pick size —"
          value={sizeIdValue}
          options={canType ? [...sizeOptions, { code: NEW_SIZE, display: '+', label: 'new size…' }] : sizeOptions}
          openSignal={cascade.signalFor('size')}
          onChange={size_id => {
            if (size_id === NEW_SIZE) { setTyping(true); return; }
            stage({ size_list: effectiveUnit, size_id, new_size_look: '' });
          }}
        />
      ) : null}
    </div>
  );
}

// onPick — STAGE the choice instead of performing it. When present it replaces
// onMoveLine entirely: the pick is handed to the caller (which buffers it) and
// nothing is written until Save.
//
// ⚠ WHY THIS PROP EXISTS. Alex, 2026-08-27: "When changing Parent in catalog, as
// soon as I select, it moves w/o having a chance to click on save." Every other
// cell in that row buffers; this one wrote straight to the server on pick, so
// Save had nothing to do and Cancel could not undo it. DataGrid's own contract
// says so in one line: "A column rendering its own editor MUST stage through
// ctx.buffer, not write straight to the server — otherwise the row auto-saves on
// click and Cancel cannot undo it." This column was the rule's oldest violator.
function CatalogReparent({ tree, l1Id, l2Id, variant, onMoveCategory, onMoveLine, onPick, disabled, onNavigate, currentL3 }) {
  const [l2, setL2] = useState(() => l2Id ?? null);
  // Seed L3 with where the item ACTUALLY IS. It used to start null, so opening
  // the editor showed "MF › M7 › —" and the level Alex was looking straight at
  // read as empty — the one level whose current value he needed to see in order
  // to change it. Read mode already shows the full path via currentL3; edit mode
  // now agrees with it instead of blanking the last step.
  const [l3, setL3] = useState(() => currentL3?.id ?? null);
  // UX-3 (2026-08-04) -- this cascade never auto-advanced. Picking the L2 shelf left
  // the L3 dropdown closed, so a move needed two clicks where every other cascade in
  // the app needs one. Alex's rule is that picking a level MUST open the next; the
  // sweep that found this also found the catalog Size picker and the placement
  // "Place into" picker in the same state.
  const cascade = useCascadeSignal();

  // DataGrid reuses a cell instance across rows, so mount-time seeding alone
  // would leave the PREVIOUS row's position showing after a selection change.
  // Re-sync whenever the item's actual position changes.
  const curL3Id = currentL3?.id ?? null;
  useEffect(() => { setL2(l2Id ?? null); setL3(curL3Id); }, [l2Id, curL3Id]);

  const l1Node = l1Id != null ? tree.find(n => n._catId === l1Id) : null;
  const l2Opts = l1Node ? (l1Node.children || []).filter(c => !c.isLine) : [];
  const l2Node = l2 != null ? l2Opts.find(n => n._catId === l2) : null;
  const l3Opts = l2Node ? (l2Node.children || []).filter(c => !c.isLine) : [];

  if (disabled) {
    // L2 shows whenever we have one to show — not gated by variant. It used to
    // be 'line'-only, which meant the L2-view's own L3-row Parent cells (variant
    // 'l3') read as just "RM" with no L2; phase 3 (2026-07-20) needed the L3
    // HEADER's Parent cell to read the full L1›L2›L3 path, and there's no reason
    // the two variants should show different amounts of the SAME path.
    const parts = [
      { label: l1Node ? l1Node.code : '—', nodeId: l1Id != null ? `c1-${l1Id}` : null },
      ...(l2Id != null
        ? [{ label: l2Node ? l2Node.code : `#${l2Id}`, nodeId: `c2-${l2Id}` }]
        : []),
      ...(currentL3 ? [{ label: currentL3.code, nodeId: `c3-${currentL3.id}` }] : []),
    ];
    const crumbs = parts.map(p => ({ label: p.label, onClick: (onNavigate && p.nodeId != null) ? () => onNavigate(p.nodeId) : undefined }));
    return <Breadcrumb dense crumbs={crumbs} />;
  }

  // Two-column {code, label} options — display carries the mono Tag column,
  // matching PathDropdowns/PlaceDropdowns' toOptions convention.
  const toOptions = opts => opts.map(n => ({ code: n._catId, label: n.label || '', display: n.code || '' }));

  const handleL2 = (code) => {
    const id = code === '' || code == null ? null : Number(code);
    setL2(id); setL3(null);
    if (variant === 'l3' && id) onMoveCategory(id, l1Node);
    // Only the 'line' variant HAS an L3 to open; on the 'l3' variant the L2 pick is
    // itself the move, so there is nothing after it.
    else if (variant === 'line' && id) cascade.advance('l3');
  };
  const handleL3 = (code) => {
    const id = code === '' || code == null ? null : Number(code);
    setL3(id);
    // Now that the level opens showing where the item already is, re-picking
    // that same shelf is not a move — don't fire a pointless reparent for it.
    if (!id || id === curL3Id) return;
    // Staged when the caller can stage it, performed only where it cannot (the
    // familyAgg row, which carries its own Done-moving toggle rather than the
    // grid's buffered Save/Cancel).
    if (onPick) onPick(id, l1Node);
    else onMoveLine(id, l1Node);
  };

  const levels = [
    {
      ariaLabel: 'Level 1 (cost classification — locked)',
      disabled: true, allowClear: false,
      value: l1Id, options: tree.map(n => ({ code: n._catId, label: n.label || '', display: n.code || '' })),
    },
    {
      ariaLabel: variant === 'l3' ? 'New level 2 parent' : 'Level 2 category',
      placeholder: '— pick parent —', allowClear: false,
      value: l2, options: toOptions(l2Opts), onChange: handleL2,
    },
  ];
  if (variant === 'line') {
    levels.push({
      ariaLabel: 'New level 3 parent',
      placeholder: '— pick parent —', allowClear: false, disabled: !l2,
      value: l3, options: toOptions(l3Opts), onChange: handleL3,
      openSignal: cascade.signalFor('l3'),
    });
  }

  return (
    <span onClick={e => e.stopPropagation()}>
      <CascadeSelect levels={levels} gap={3} />
    </span>
  );
}

// B1 — "Used in products" for a catalog line reads identically to the PRODUCT
// screen's own "Used in Products" (Code mono + Label) — imported from
// usage-cols.jsx rather than retyped; see CATALOG_PLACED_IN_COLS just below
// for why "Placed in functions" stays its own definition instead.

// G3 (batch G) — "Placed in functions" columns: where the using PRODUCTS are
// placed (project → function → placement). Mirrors ProductsView's
// PLACED_IN_PROJECTS_COLS, plus a Product column since this table can span
// several different products (each using the same catalog line).
const CATALOG_PLACED_IN_COLS = [
  {
    key: 'project_code', label: 'Project', width: 100,
    render: row => <Text kind="code">{row.project_code || '—'}</Text>,
  },
  { key: 'function_path', label: 'Function', width: 240, render: row => row.function_path || '—' },
  { key: 'placement_label', label: 'Placement', width: 160, render: row => row.placement_label || '(no label)' },
  {
    key: 'product_code', label: 'Product', width: 110,
    render: row => <Text kind="code">{row.product_code || '—'}</Text>,
  },
];

// ---------------------------------------------------------------------------
// CatalogWhereUsedTable — "Used in products" / "Placed in functions" for the
// selected catalog line, as two independent cards (2026-08-20, same shape as
// ProductsView's own Where-used pair and Function's UsageCards: closed by
// default, opening EITHER chevron loads the one shared `/usage` fetch, the
// other card's count/rows appear the moment it resolves). Covers both
// non-sized (ref_id = line id) and sized (ref_id = fam:{family_id})
// references — the server resolves both. Rows navigate to the product on
// click via onNavigateProduct.
//
// `key={lineId}` on both cards — switching lines resets `usage` below, and
// remounting re-reads each card's persisted open/closed preference from
// localStorage and (if still open) fires `onOpen` again for the new line,
// which is what actually re-triggers the fetch. Without the remount, a card
// left open while browsing would keep showing the PREVIOUS line's rows: its
// own `open` value never changes just because `lineId` did.
//
// G3 (batch G) — "Placed in functions" lists where the using PRODUCTS are
// placed (project → function → placement), from the same /usage response
// (extended server-side with `placed_in`/`truncated`). Rows navigate to the
// placement via onNavigateToPlacement (same pathway ProductsView's Where-used
// pair uses — App owns view switching).
// ---------------------------------------------------------------------------
function CatalogWhereUsedTable({ lineId, onNavigateProduct, onNavigateToPlacement }) {
  const [usage, setUsage] = useState(null); // { used_by, placed_in, truncated } | null
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setUsage(null);
    setLoading(false);
  }, [lineId]);

  const load = useCallback(() => {
    if (lineId == null || loading) return;
    setLoading(true);
    fetch(`/api/catalog/lines/${lineId}/usage`)
      .then(r => r.ok ? r.json() : { used_by: [], placed_in: [], truncated: false })
      .then(u => setUsage(u))
      .catch(err => { console.error(err); setUsage({ used_by: [], placed_in: [], truncated: false }); })
      .finally(() => setLoading(false));
  }, [lineId, loading]);

  // Always mounted, closed by default (Alex, 2026-08-20: "where used should be
  // closed and visible" — not absent). With nothing selected, the KEY
  // INFORMATION in the title row says so directly ("nothing selected") instead
  // of a count, and opening either card explains itself rather than fetching.
  const noSelection = lineId == null;
  const usedBy = usage?.used_by ?? [];
  const placedIn = usage?.placed_in ?? [];
  const rows = usedBy.map(p => ({ ...p, id: p.product_id }));
  const placedRows = placedIn.map((p, i) => ({ ...p, id: p.placement_id ?? i }));
  const onOpen = () => { if (!noSelection && usage == null) load(); };
  const selectHint = (
    <Text kind="note" as="div">
      Select a line to see where it is used.
    </Text>
  );

  return (
    <>
      <Card
        key={lineId ?? 'none'}
        storageKey="catalog:section:used-in-products"
        title="Used in products"
        count={noSelection ? 'nothing selected' : (usage != null ? rows.length : undefined)}
        defaultOpen={false}
        onOpen={onOpen}
      >
        {noSelection ? selectHint : usage == null ? (
          <Text kind="note" as="div">Loading usage…</Text>
        ) : (
          <>
            <DataGrid
              rows={rows}
              columns={USED_IN_PRODUCTS_COLS}
              onRowClick={onNavigateProduct ? (row) => onNavigateProduct(row.product_id) : undefined}
              emptyMessage="Not used by any product — safe to delete."
              storageKey="catalog-line-usage"
            />
            {(usage?.legacy_parts?.length ?? 0) > 0 && (
              <Text kind="warn" as="div" style={{ marginTop: 4 }}>
                {WARN} Referenced by {usage.legacy_parts.length} legacy part row(s) from the retired
                Parts spike ({usage.legacy_parts.map(p => p.display_name || p.part_ref).join(', ')})
                — blocks deletion until cleaned.
              </Text>
            )}
          </>
        )}
      </Card>
      <Card
        key={lineId != null ? `${lineId}-placed` : 'none-placed'}
        storageKey="catalog:section:placed-in-functions"
        title="Placed in functions"
        count={noSelection ? 'nothing selected' : (usage != null ? `${placedRows.length}${usage?.truncated ? '+' : ''}` : undefined)}
        defaultOpen={false}
        onOpen={onOpen}
      >
        {noSelection ? selectHint : usage == null ? (
          <Text kind="note" as="div">Loading usage…</Text>
        ) : (
          <>
            {usage?.truncated && (
              <Text kind="note" as="div" style={{ marginBottom: 4 }}>
                Showing the first 200 placements — narrow the usage to see more.
              </Text>
            )}
            <DataGrid
              rows={placedRows}
              columns={CATALOG_PLACED_IN_COLS}
              onRowClick={onNavigateToPlacement ? (row) => onNavigateToPlacement(row) : undefined}
              emptyMessage="Not placed in any project function."
              storageKey="catalog-line-placed-in"
            />
          </>
        )}
      </Card>
    </>
  );
}

export default function CatalogView({
  // view/setView — App's nav state, threaded through ONLY to fill the
  // ViewSwitcher (NIGHT_SHIFT item 14, 2026-07-23); undefined on an embedded
  // Onshape Main (hideNav), where HierarchyWorkspace suppresses treeHeader anyway.
  view,
  setView,
  selectedNodeId,
  onSelectNode,
  initialFamilyId = null,
  activeProjectId = '',
  catalogTarget = null,
  onNavigateToProduct,
  onNavigateToPlacement,
  // Embedded mode (Onshape screen renders this Main with its own tree hidden).
  hideNav = false,
}) {
  const [tree, setTree] = useState([]);
  const [lines, setLines] = useState([]);
  // Cleanup batch (Alex 2026-07-03): moved from the top banner to the Catalog screen's
  // own header. Default (and first load): ALL statuses ON except deprecated OFF.
  // NOT persisted (Alex, 2026-07-18: "I do not need the remember behavior. We
  // leave the grid, filter becomes off") — the filter is a per-grid control that
  // always starts at the default and resets on leaving the grid (effect below).
  // A remembered filter is what silently emptied the next L3 and made the tree
  // look random.
  const [statusFilter, setStatusFilter] = useState(DEFAULT_STATUS_FILTER);
  const [loading, setLoading] = useState(false);
  // ERR-1 — the failure message for a grid ACTION (save / copy / delete / move).
  // Was 11 alert() popups: browser chrome, blocking, detached from the row, and
  // gone the moment you dismiss them. Now it renders in the app, above the grid
  // it belongs to, and stays until dismissed or superseded — so it is still
  // readable while you fix the thing it is complaining about.
  const [actionError, setActionError] = useState(null);
  // INF-8 — says, once, that an add copied a Standard item into the project.
  const [copyNotice, setCopyNotice] = useState(null);
  // ERR-1(b) — a line's failure is NOT tracked here: handleUpdateLine throws and DataGrid
  // places the reason under the line. actionError stays for view-level failures with no
  // row to sit under (a category that would not load).
  // ...and it is scrolled to when it appears. Rendering it in the right PLACE is
  // only half the job: the grid is long, so on a row far down the message landed
  // above the fold and a refused action still read as "nothing happened".
  const actionErrorRef = useRef(null);
  useEffect(() => {
    if (actionError) actionErrorRef.current?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }, [actionError]);
  // ERR-1 / COS-6 — in-app confirmation. window.confirm was synchronous, which is
  // why the old code could branch on it inline; a rendered panel cannot be, so the
  // question becomes a promise the panel resolves. `askConfirm` returns the CHOSEN
  // VALUE (or null for Cancel), which is what lets one question carry three
  // answers instead of yes/no.
  const [pendingConfirm, setPendingConfirm] = useState(null);
  const askConfirm = (opts) => new Promise(resolve => setPendingConfirm({ ...opts, resolve }));
  const resolveConfirm = (value) => {
    setPendingConfirm(prev => { prev?.resolve(value); return null; });
  };
  const [selectedLineId, setSelectedLineId] = useState(null);       // line leaf sub-row open
  const [selectedRowId, setSelectedRowId] = useState(null);          // grid row highlight (single-click)
  const [editRowId, setEditRowId] = useState(null);                  // grid row in edit mode
  // A multi-size FAMILY row in edit mode (Tag + Label only — note 8, 2026-10-11).
  // Its own state, not editRowId: the custom size/material/parent cells key their
  // editors off editRowId, and a family row has no single size for them to edit.
  const [editAggId, setEditAggId] = useState(null);
  // B2 — family leaf selected in the tree: filters the L3's grid down to that
  // family's size rows (client-side). null = show ALL lines under the L3 (unchanged).
  // Seeded from a `?node=c3-N|fam-M` deep link so the family panel opens on
  // arrival — see the selectedNodeId note above. The [selectedNodeId] effect below
  // clears this on navigation, which is correct: it only fires on a CHANGE, so the
  // initial value survives the first render.
  // Deep-link family seed (`?node=c3-N|fam-M`) — read once via the prop; the
  // navSettled guard below keeps it from being cleared before the tree arrives.
  const [selectedFamilyId, setSelectedFamilyId] = useState(() => initialFamilyId ?? null);
  // A multi-size family's OWN move toggle (UX-40) — keyed by family_id, not a
  // row id, because it drives the aggregate row's Parent cell, which has no
  // single line id of its own. Independent of the grid's shared editRowId —
  // see the _move column's render for why it can't share that toggle.
  const [editFamilyRowId, setEditFamilyRowId] = useState(null);
  // The Sizes card's own open state (UX-40, Alex 2026-08-21: "when a family
  // with size, open by default on selection"). Deliberately NOT Card's own
  // persisted storageKey — that remembers ONE preference per SURFACE, shared
  // across every family, so closing it once on family A would stay closed
  // for every family after. This is per-SELECTION instead: controlled, and
  // reset to open every time selectedFamilyId changes (below); a manual
  // collapse lasts only until the next family is picked.
  const [sizesOpen, setSizesOpen] = useState(true);
  // A click on "N sizes" or the 3D cell shows a card BELOW the grid; this brings
  // it into view after it renders. { to: 'sizes' | 'model', t } — t makes a
  // repeat click on the same family scroll again.
  const [scrollTarget, setScrollTarget] = useState(null);
  // A family picked from OUTSIDE the grid — Onshape's "Open in DB" (a deep link,
  // so it arrives as initialFamilyId) or the tree — should show its row the way a
  // click does: highlighted, and scrolled to (Alex, 2026-09-30: "it will be cool to
  // have the family row selected (like grey as when I select myself)"). Set here and
  // in handleSelectNode; consumed once the shelf's rows have loaded.
  const revealFamilyRow = useRef(initialFamilyId != null);
  const sizesCardRef = useRef(null);
  const modelCardRef = useRef(null);
  useEffect(() => {
    if (!scrollTarget) return;
    const el = (scrollTarget.to === 'model' ? modelCardRef : sizesCardRef).current;
    el?.scrollIntoView({ block: 'start', behavior: 'smooth' });
  }, [scrollTarget]);
  useEffect(() => { setSizesOpen(true); }, [selectedFamilyId]);

  const [catalogUsage, setCatalogUsage] = useState(null); // { byLine, byFamily, total, skipped } | null

  const [materialOptions, setMaterialOptions] = useState([]);

  // Size-list picker (Spike, 2026-07-13) — Size Unit choices (size_lists.list_name
  // values), loaded once. Mirrors ProductsView.jsx's sizeUnitNames (same endpoint).
  const [sizeUnitNames, setSizeUnitNames] = useState([]);
  useEffect(() => {
    fetch('/api/products/options/size-units').then(r => r.json()).then(n => setSizeUnitNames(Array.isArray(n) ? n : [])).catch(console.error);
  }, []);

  // Resolved {value,label,nps} options per standard size_unit (value=size_id,
  // label=look), fetched lazily and cached by list_name — mirrors ProductsView.jsx's
  // sizeListValuesCache/fetchSizeListValues. 'custom' never hits this cache.
  const [sizeListValuesCache, setSizeListValuesCache] = useState({});
  // A row just created on a FULL size list: its Size cell opens as a box holding the
  // guessed next size ({ rowId, guess }). See SizeEditCell.
  const [pendingNewSize, setPendingNewSize] = useState(null);
  // A row waiting for its size opens straight in edit mode with the box focused.
  const openForNewSize = (line) => {
    if (line?._new_size_guess === undefined) return;
    setPendingNewSize({ rowId: line.id, guess: line._new_size_guess });
    setEditRowId(line.id);
  };
  const _sizeListFetched = useRef(new Set());
  const fetchSizeListValues = useCallback((name) => {
    if (!name || name === 'custom' || _sizeListFetched.current.has(name)) return;
    _sizeListFetched.current.add(name);
    fetch(`/api/products/options/size-list-values?name=${encodeURIComponent(name)}`)
      .then(r => r.json())
      .then(opts => setSizeListValuesCache(p => ({ ...p, [name]: Array.isArray(opts) ? opts : [] })))
      .catch(err => { console.error(err); _sizeListFetched.current.delete(name); });
  }, []);
  // Prefetch every distinct size_list present among the loaded lines, so looks
  // render without a click.
  useEffect(() => {
    const units = new Set(lines.filter(l => l.size_list && l.size_list !== 'custom').map(l => l.size_list));
    units.forEach(fetchSizeListValues);
  }, [lines, fetchSizeListValues]);

  // Trades — one source of truth (Q5). Own copy for the FabProductCosting
  // dropdown (same pattern as ProductsView); the app-level setTradeRates feed
  // for the client resolver stays in App.jsx.
  const [tradeOptions, setTradeOptionsState] = useState([]);
  useEffect(() => {
    fetch('/api/options/trades').then(r => r.json()).then(list => {
      setTradeOptionsState(Array.isArray(list) ? list : []);
    }).catch(console.error);
  }, []);

  const treeValidated = useRef(false);

  // Cross-screen navigation INTO the catalog ({l1,l2,l3,token} by CODE — e.g.
  // ProductsView's "open in catalog" chips). Replaces App reaching into this
  // screen's tree; consumed once per token, and only once the tree can resolve
  // it (re-runs when the tree arrives).
  const consumedTargetToken = useRef(null);
  useEffect(() => {
    if (!catalogTarget || catalogTarget.token === consumedTargetToken.current) return;
    if (!tree.length) return;
    const target = findCatByPath(tree, catalogTarget.l1, catalogTarget.l2, catalogTarget.l3);
    if (target) {
      consumedTargetToken.current = catalogTarget.token;
      handleSelectNode(target.id);
    }
  }); // eslint-disable-line react-hooks/exhaustive-deps — guard-first, cheap, per render

  // Load distinct material values once (for datalist)
  useEffect(() => {
    fetch('/api/catalog/options/materials').then(r => r.json()).then(setMaterialOptions).catch(() => {});
  }, []);

  // Catalog-usage rollup for the app-level active project (persistence of the
  // choice itself stays in App — the banner owns it).
  useEffect(() => {
    if (!activeProjectId) { setCatalogUsage(null); return; }
    fetch(`/api/projects/${activeProjectId}/catalog-usage`)
      .then(r => r.ok ? r.json() : null)
      .then(setCatalogUsage)
      .catch(() => setCatalogUsage(null));
  }, [activeProjectId]);

  // composeTree() in degenerate single-source form — the SAME composition
  // function OnshapeView's composite tree is built from (tree-model.js, Alex
  // 2026-07-20: "the tree used in Onshape [should] be the leader"). A
  // single-source call is a pure passthrough (same array, same ids), so this
  // is a no-op wrapper today; it's what keeps the composition logic in ONE
  // place if a second source is ever needed here.
  const refreshTree = useCallback(() => {
    return fetch('/api/catalog/tree').then(r => r.json())
      .then(catTree => setTree(composeTree({ catalog: catTree })))
      .catch(console.error);
  }, []);

  useEffect(() => { refreshTree(); }, [refreshTree]);

  // --- Tree lookup helpers ----------------------------------------------------
  const findNode = useCallback((nodeId, nodes = tree) => {
    for (const n of nodes) {
      if (n.id === nodeId) return n;
      if (n.children && !n.isLine) { const r = findNode(nodeId, n.children); if (r) return r; }
    }
    return null;
  }, [tree]);

  const findPath = useCallback((nodeId, nodes = tree, trail = []) => {
    for (const n of nodes) {
      const next = [...trail, n];
      if (n.id === nodeId) return next;
      if (n.children && !n.isLine) { const r = findPath(nodeId, n.children, next); if (r) return r; }
    }
    return null;
  }, [tree]);

  // navTree — what the LEFT NAV actually renders. UX-40 (Alex, 2026-08-21):
  // "I would like to totally remove that [family] level from the tree because
  // I think now it is becoming useless" — the family grid + Sizes card do
  // everything family/line selection used to need the tree for, so an L3's
  // own line/family leaves (isLine — B2, catalog-db.js) are dropped here.
  //
  // DISPLAY-ONLY: `tree` itself is untouched, on purpose. selectedFamilyNode
  // below still reads a family's data (onshape_ref/onshape_config/label/…)
  // off `tree`, and OnshapeView's own tree (its `linkedOnly` composite,
  // tree-model.js's pruneTree/isLeafNode) and OnshapePanel's family-picker
  // cascade both still need those same leaves for their own, separate
  // reasons — neither of them reads navTree, only this screen's own
  // HierarchyWorkspace does.
  // PROJECT ONLY (Alex, 2026-10-15) — working in a project, the tree and grid start as
  // that project's OWN rows; the "Standard" box (ShowStandardCheck) brings the rest back.
  // Display-only, like navTree: `tree` stays whole, so lookups and deep links resolve.
  // Off inside the Onshape screen (hideNav): it has no tree header to show the box in.
  const { workingIn, workingCode } = useContext(WorkingInContext);
  const [showStandard, setShowStandard] = useState(false);
  const projectOnly = !!workingIn && !showStandard && !hideNav;
  const shownTree = useMemo(() => (projectOnly ? ownOnlyTree(tree, workingIn) : tree), [projectOnly, tree, workingIn]);
  const shownIds = useMemo(() => {
    if (!projectOnly) return null;
    const ids = new Set();
    const walk = (ns) => { for (const n of ns) { ids.add(n.id); walk(n.children || []); } };
    walk(shownTree);
    return ids;
  }, [projectOnly, shownTree]);

  const navTree = useMemo(() => {
    const prune = (nodes) => nodes
      .filter(n => !n.isLine)
      .map(n => ({ ...n, children: prune(n.children || []) }));
    return prune(shownTree);
  }, [shownTree]);

  // The selected category node (null when root or a line leaf is selected).
  const selectedCat = useMemo(() => (isCatNodeId(selectedNodeId) ? findNode(selectedNodeId) : null), [selectedNodeId, findNode]);
  const selectedLevel = selectedCat?.level ?? null;
  const isL3 = selectedLevel === 3;

  // The one grid's card title — same branches as its own emptyMessage below, so
  // the two can never disagree about what's on screen (Alex, 2026-08-20).
  const gridCardTitle = isL3 ? 'Family' : selectedCat ? 'Sub-categories' : 'Categories';

  // Active L3 category id (for size datalist + add-line).
  const activeL3Id = isL3 ? selectedCat._catId : null;

  // Size-list picker (Spike, 2026-07-13) — per-family lock: the first non-null
  // size_list among a family's already-loaded rows. A family is "already sized"
  // (Size Unit locked) whenever this is truthy; a freshly-added line in a sized
  // family inherits the lock via its shared family_id.
  const familySizeListById = useMemo(() => {
    const map = {};
    for (const l of lines) {
      if (l.family_id == null || map[l.family_id]) continue;
      // ⚠ 'unique' DOES NOT COUNT AS SIZED. It is the sentinel for "this family has
      // no size axis" (schema: size_id is NOT NULL, so a size-less row carries it),
      // and treating it as "already sized" locked the Size Unit on exactly the
      // families you would want to give sizes to. Alex, 2026-08-09: "I would like to
      // update piping per NPS. But cannot play with the size!" — R2ISP was locked at
      // 'unique', and because 'unique' is deliberately absent from the pickable units
      // (listSizeListNames excludes it) the control ALSO rendered blank. A dropdown
      // showing nothing and refusing to open is the worst of both. This is Q14.3.
      if (l.size_list && l.size_list !== 'unique') map[l.family_id] = l.size_list;
    }
    return map;
  }, [lines]);

  // Lines per family in the loaded L3 — a MULTI-SIZE family's member rows lose
  // their individual Parent editor (Alex, 2026-07-20: the family moves as one,
  // from the family HEADER row only; the server reparents siblings together
  // anyway, so a per-size move control was misleading).
  const familyLineCount = useMemo(() => {
    const m = {};
    for (const l of lines) if (l.family_id != null) m[l.family_id] = (m[l.family_id] || 0) + 1;
    return m;
  }, [lines]);

  // Family-level Onshape linking (2026-07-03) — the selected family's tree leaf
  // node (carries onshape_ref/onshape_config from listCatalogTree; see
  // src/catalog-db.js). Null unless a family is currently selected.
  const selectedFamilyNode = useMemo(() => {
    if (selectedFamilyId == null || !selectedCat?.children) return null;
    return selectedCat.children.find(c => c.isFamily && c.family_id === selectedFamilyId) || null;
  }, [selectedCat, selectedFamilyId]);

  // #2 — ancestor catIds of the selected node (for same-L1 re-parent dropdowns).
  // selectedL1Id/selectedL2Id are the L1/L2 catIds on the path to selectedCat.
  const selectedAncestors = useMemo(() => {
    const path = selectedCat ? (findPath(selectedNodeId) || []) : [];
    const byLevel = lvl => { const n = path.find(p => p.level === lvl); return n ? n._catId : null; };
    return { l1Id: byLevel(1), l2Id: byLevel(2) };
  }, [selectedCat, selectedNodeId, findPath]);

  // L1 cost_class on the SELECTED path — 'HR' is labour, which can never have a
  // CAD model, so the family's Onshape section renders #NA there instead of
  // offering a link (UI_VISION §4). Same derivation the columns memo makes for
  // its own #NA cells (`curL1Class`); hoisted here because the family panel
  // renders outside that memo. MUST stay below `selectedAncestors` — declaring
  // it above threw a temporal-dead-zone ReferenceError that blanked the whole
  // screen while the build stayed green.
  const selectedL1Class = useMemo(() => {
    const n = selectedAncestors.l1Id != null ? tree.find(x => x._catId === selectedAncestors.l1Id) : null;
    return n ? n.cost_class : null;
  }, [selectedAncestors.l1Id, tree]);

  // Load lines for the selected L3 category (by category_id) + status filter.
  // Status is NOT sent to the server any more (Alex, 2026-07-18): the chips
  // EMPHASISE rather than hide, so the grid always holds every line of the L3.
  // That's what kills the tree/grid mismatch — the tree is unfiltered, so if the
  // grid can't hide anything either, a visible tree node can never render empty.
  // Bonus: changing a chip is now pure client-side styling — no refetch.
  const loadLines = useCallback((catId) => {
    if (!catId) { setLines([]); return; }
    setLoading(true);
    fetch(`/api/catalog/categories/${catId}/lines`)
      .then(r => r.json())
      .then(data => { setLines(Array.isArray(data) ? data : []); setLoading(false); })
      // ERR-1: a load failure was console-only, so the grid just sat empty with no
      // reason given. Say it on screen; the console line stays for diagnosis.
      .catch(err => { console.error(err); setActionError(`Could not load this category: ${err.message}`); setLoading(false); });
  }, []);

  useEffect(() => { loadLines(activeL3Id); }, [activeL3Id, loadLines]);

  // Reset the status filter whenever the GRID changes — i.e. a different L3
  // (Alex, 2026-07-18: "these filters needs to reset when we change grid on the
  // tree. Reset is deprecated off"). Selecting a family/line leaf WITHIN the
  // same L3 keeps the same grid, so it does NOT reset. No mount guard needed:
  // the filter isn't persisted, so on mount it already IS DEFAULT_STATUS_FILTER
  // and setting the same module-level reference is a no-op (React bails out).
  useEffect(() => {
    setStatusFilter(DEFAULT_STATUS_FILTER);
  }, [activeL3Id]);

  // Clear transient selections on navigation.
  // Clear transient selections when the user NAVIGATES. Skips the first run: a
  // `?node=…|fam-N` deep link seeds selectedFamilyId, and a mount-time clear would
  // wipe it before the tree even arrives (it did — the panel stayed shut).
  const navSettled = useRef(false);
  useEffect(() => {
    if (!navSettled.current) { navSettled.current = true; return; }
    setSelectedLineId(null); setSelectedRowId(null); setEditRowId(null); setEditAggId(null); setSelectedFamilyId(null);
    setEditFamilyRowId(null);
  }, [selectedNodeId]);

  // Graceful fallback: drop a restored selection that no longer exists once tree arrives.
  // (Persistence of the selection itself lives in App — the state owner.)
  useEffect(() => {
    if (!tree.length || treeValidated.current) return;
    treeValidated.current = true;
    if (!selectedNodeId) return;
    if (isAnyLineLeafId(selectedNodeId)) {
      const catNodeId = catNodeIdOfLeaf(selectedNodeId);
      if (!findNode(catNodeId)) onSelectNode(null);
    } else if (isCatNodeId(selectedNodeId)) {
      if (!findNode(selectedNodeId)) onSelectNode(null);
    }
  }, [tree]); // eslint-disable-line react-hooks/exhaustive-deps

  // --- Mutation handlers ------------------------------------------------------

  // Line field update (path-neutral: label/price/size/etc).
  // A Tag change can come back 409 + `confirm` — AMBIGUOUS rather than wrong
  // (Alex, 2026-07-21: "instead of having a very basic error message… propose to
  // me to confirm"). The server does the thinking; this only asks the question
  // and re-sends the answer as `_tagIntent`. See renameLineL4 in catalog-db.js.
  // Returns the consequences as a LIST, not one glued string: ConfirmPanel renders
  // them as bullets, so they read as separate consequences instead of the wall of
  // text window.confirm forced (it could only take \n).
  const describeImpact = (imp) => {
    if (!imp) return [];
    const out = [];
    // Onshape FIRST and separately: it is the only consequence we cannot repair
    // from here (read-only, stamped by hand), so it must not be buried in counts.
    if (imp.onshapeRef && imp.sourceFamilyEmptied) {
      out.push(`${WARN} ONSHAPE: this item is stamped in Onshape, and after this move its family will have no rows left. `
        + `The stamped db_id will point at nothing until YOU re-stamp it in Onshape — the app cannot fix that.`);
    }
    if (imp.pathRefs) {
      out.push(`${WARN} ${imp.pathRefs} component(s) reference this item BY PATH (including the tag). They will silently resolve to nothing.`);
    }
    if (imp.familyRefs && imp.sourceFamilyEmptied) {
      out.push(`${WARN} ${imp.familyRefs} component(s) point at the family being emptied. They will silently resolve to nothing.`);
    }
    if (imp.safeRefs) {
      out.push(`${imp.safeRefs} component(s) reference the row by id — those follow it automatically, nothing to redo.`);
    }
    if (!out.length) out.push('Nothing else references this row.');
    return out;
  };

  const handleUpdateLine = async (id, fields) => {
    const send = (body) => fetch(`/api/catalog/lines/${id}`, {
      method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
    });
    let res = await send(fields);

    if (res.status === 409) {
      const { error, confirm } = await res.json();
      if (!confirm) { setActionError(error); return; }
      let intent = null;
      if (confirm.kind === 'merge') {
        const sizes = confirm.targetSizes.length ? ` (sizes ${confirm.targetSizes.join(', ')})` : '';
        const mine = confirm.movingSizes.length ? confirm.movingSizes.join(', ') : 'no size';
        const choice = await askConfirm({
          title: `Tag ${confirm.newTag} already exists`,
          question: `${confirm.newTag} is already "${confirm.targetLabel}"${sizes}.\n`
            + `This row is ${mine} — no clash, so it can join that item.`,
          impact: describeImpact(confirm.impact),
          actions: [{ label: `Move into "${confirm.targetLabel}"`, value: 'merge' }],
        });
        if (choice !== 'merge') return;
        intent = { allowMerge: true };
      } else if (confirm.kind === 'scope') {
        // THREE outcomes, asked once (Alex chose this 2026-07-26).
        //
        // The previous note here explained that a yes/no box "cannot express three
        // outcomes", so the two intents were split across two controls — this row's
        // Tag meant SPLIT, and re-tagging the whole item meant cancelling and
        // editing the Family header instead. That was the right answer to
        // window.confirm's limit. The limit is gone, so the third answer moves back
        // to where the question is actually asked.
        //
        // Split stays FIRST (primary) — that keeps the copy→rename workflow the
        // server comments describe (H03A copy → H03C) as the default action, which
        // is what the old note was protecting. Editing the Family header row still
        // works exactly as before; this only saves a cancel-and-go-elsewhere.
        // The OPTIONS COME FROM THE SERVER — it has always sent both, with labels,
        // in the 409 payload (`confirm.options`); window.confirm could only render
        // one of them, so the client threw the other away. Reading them here keeps
        // one source of truth for the wording and the accepted values, and a third
        // option added server-side would appear with no change on this side.
        const opts = confirm.options?.length
          ? confirm.options
          : [{ value: 'row', label: `Split this row out as ${confirm.newTag}` },
             { value: 'family', label: `Re-tag all ${confirm.siblingCount + 1} rows to ${confirm.newTag}` }];
        const choice = await askConfirm({
          title: `"${confirm.currentTag}" covers ${confirm.siblingCount + 1} size rows`,
          question: 'Re-tag just this row, or the whole item?',
          impact: describeImpact(confirm.impact),
          // 'row' stays first/primary: it is the copy→rename workflow the server
          // comments describe (H03A copy → H03C), and the old note was right to
          // protect it as the default. 'family' is marked danger — it moves every
          // sibling at once.
          actions: opts.map(o => ({ ...o, tone: o.value === 'family' ? 'danger' : undefined })),
        });
        if (!choice) return;
        intent = { scope: choice };   // 'row' | 'family' — both already accepted server-side
      }
      if (!intent) return;
      res = await send({ ...fields, _tagIntent: intent });
      if (res.ok) await refreshTree();
    }

    // ERR-1(b), extended to the catalog (Alex, 2026-08-05: renaming a Tag to more than
    // three characters "failed silently"). It was not silent — `Invalid Tag "TTX1" —
    // expected 1-3 letters/digits` rendered at the TOP of the page, some 700px above the
    // row being edited, on a 16-row shelf. A message you cannot see while typing is
    // indistinguishable from a control that does nothing.
    if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.error || 'Save failed'); }
    const updated = await res.json();
    setLines(prev => prev.map(l => l.id === id ? { ...l, ...updated } : l));
    if (fields.status) refreshTree();
  };

  // Category label update.
  const handleUpdateCategory = async (id, fields) => {
    const res = await fetch(`/api/catalog/categories/${id}`, {
      method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(fields),
    });
    // Throws like saveFamilyLabel, so a refused category rename lands under the row that
    // was renamed rather than at the top of the page.
    if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.error || 'Save failed'); }
    await refreshTree();
  };

  // DM-5 — the one write path for an item's NAME. Called from the size row's
  // Label cell AND from the family header row, because they are the same field:
  // a family has one name and all its sizes read it. Reloads the lines so the
  // sibling rows repaint with the new name in the same beat.
  const saveFamilyLabel = async (famId, value) => {
    const res = await fetch(`/api/catalog/families/${famId}`, {
      method: 'PATCH', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ label: value }),
    });
    if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.error || 'Save failed'); }
    if (activeL3Id != null) loadLines(activeL3Id);
    await refreshTree();
  };

  // DataGrid onSave: (rowId, field, value). Routes by row type.
  // async + RETURNS every promise: DataGrid awaits onSave and places a rejection under the
  // row, so a handler that fires-and-forgets would turn a real refusal into an unhandled
  // rejection in the console and a screen that looks like nothing happened.
  const handleSave = async (rowId, field, value) => {
    // UX-40 — gridRows (top grid) no longer carries every size once a family
    // has more than one; lineRows (unfiltered) backs both the top grid's n=1
    // rows AND the Sizes card's rows, so it's the one lookup that always finds
    // the row a save came from, regardless of which grid it came from.
    // Note 8 (Alex, 2026-10-11: "When different sizes exist, the tag, label must be
    // editable in the main row vs. the size row. Size row is for size dependent
    // information"). Both are FAMILY fields, so the family row writes the family.
    if (isL3 && String(rowId).startsWith('fam-')) {
      const fam = gridRows.find(r => String(r.id) === String(rowId));
      if (!fam) return undefined;
      if (field === 'label') return saveFamilyLabel(fam.family_id, value);
      if (field === 'code') {
        let l4 = String(value || '').trim().toUpperCase();
        if (fam.l3 && l4.startsWith(fam.l3)) l4 = l4.slice(fam.l3.length);
        const res = await fetch(`/api/catalog/families/${fam.family_id}`, {
          method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ l4 }),
        });
        if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.error || 'Save failed'); }
        loadLines(activeL3Id);
        await refreshTree();
      }
      return undefined;
    }
    const row = (isL3 ? lineRows : gridRows).find(r => String(r.id) === String(rowId));
    if (!row) return;
    if (row._type === 'line') {
      // Line 'code' displays as l3+l4 — the user can type either the full code
      // ("M12C") or just the l4 letter ("C"); strip the l3 prefix if present and
      // PATCH only l4 (the server cascades the code-group + family, guarded
      // against colliding with a different item's code).
      if (field === 'code') {
        let l4 = String(value || '').trim().toUpperCase();
        if (row.l3 && l4.startsWith(row.l3)) l4 = l4.slice(row.l3.length);
        return handleUpdateLine(rowId, { l4 });
      }
      // DM-5 — Label is the FAMILY's name, not this row's. Editing it on any
      // size row renames the whole item, which is what Alex expected the
      // control to do. Unconditional: every line has a family_id (0 nulls of
      // 1283), and on one of the 96 families still named after its tag this
      // write is precisely the fix — the real name lands on the family and
      // `_family_label` starts resolving for every one of its rows.
      if (field === 'label' && row.family_id != null) {
        return saveFamilyLabel(row.family_id, value);
      }
      // The staged Parent move (see the '_move' column). It arrives here like any
      // other buffered cell, which is the whole point — Save performs it, and
      // Cancel means it never arrives at all.
      //
      // ⚠ ROUTING, NOT A FIELD WRITE, and the routing is the dangerous half.
      // Moving the only line of a family IS moving the family; sending it down
      // the line-level path instead "is what emptied family 489" (see the column
      // comment above). This is the SAME branch that ran on pick before — only
      // WHEN it runs has changed.
      if (field === '_move') {
        const newL3Id = Number(value);
        if (!newL3Id || newL3Id === row._move) return undefined;
        const newL1Node = tree.find(n => (n.children || []).some(
          l2 => (l2.children || []).some(l3 => l3._catId === newL3Id)));
        // ⚠ THROW ON REFUSAL, because DataGrid's contract says so: "What onSave
        // must do: THROW (or reject) on failure. Returning quietly [reads as
        // success]." Found live — moving RBD into RBX is refused by the server
        // ("a family tagged RBD already exists there"), and because the handlers
        // report by setActionError + return, the buffered Save treated a 400 as
        // a clean save: buffer cleared, edit mode exited, the item still where it
        // was. Throwing keeps the row open with the reason beside the Save button,
        // which is where a rejected value can actually be corrected.
        return row.family_id != null
          ? handleFamilyMove(row.family_id, newL3Id, newL1Node)
          : handleReparentLine(row.id, newL3Id, newL1Node);
      }
      // Efficiency/Productivity/Price edit via a FORMULA cell (NIGHT_SHIFT.md
      // items 4 + 5): `field` here is the '*_expr' column, not the
      // plain numeric column, so the raw expression text goes straight to the
      // server — catalog-db.js updateLine evaluates it (evalFormula) and
      // stores BOTH the number and the expression, or throws (surfaced via
      // handleUpdateLine's existing alert(e.error)) without touching the
      // previously-stored number.
      // A typed size may have ADDED a size to the list — refetch the list so the
      // picker offers it, on this row and on every other item using the list.
      if (field === 'new_size_look') {
        await handleUpdateLine(rowId, { new_size_look: value });
        const list = familySizeListById[row.family_id] || row.size_list;
        _sizeListFetched.current.delete(list);
        fetchSizeListValues(list);
        setPendingNewSize(null);
        return;
      }
      return handleUpdateLine(rowId, { [field]: value });
    } else {
      // Categories: label OR code (code → cascading rename via PATCH …/:id).
      if (field === 'code') { return handleUpdateCategory(rowId, { code: value }); }
      // The Flag (is_material / is_labor) is a SHELF property, so THIS is where
      // it is edited — on the L3 row itself, not on every line underneath it.
      // Confirm with the row count, because one click re-classifies everything
      // on the shelf: flagging RM makes its rows selectable as material prices,
      // unflagging removes them from the picker wherever they are already used.
      if (field === 'is_material' || field === 'is_labor') {
        const on = value ? 1 : 0;
        const n = row.count ?? 0;
        const what = field === 'is_material' ? 'a raw-material source' : 'a labour rate';
        // THE LAST window.confirm IN src/web, retired 2026-08-13 (G6). ConfirmPanel
        // and `askConfirm` were already in THIS FILE, ~400 lines above; the browser
        // box survived only because nothing failed while it was there. Consequences
        // now go in `impact` as a LIST instead of being glued together with \n\n,
        // which is the whole reason ConfirmPanel exists (ERR-1 / COS-6).
        const choice = await askConfirm({
          title: `${on ? 'Flag' : 'Un-flag'} shelf ${row.code} as ${what}`,
          question: `"${row.label}" ${on ? 'becomes' : 'stops being'} ${what}.`,
          impact: [
            `Applies to the WHOLE shelf — ${n} item${n === 1 ? '' : 's'}, not this one row.`,
            on
              ? 'Its rows become selectable in the picker.'
              : 'Its rows leave the picker, including anywhere already using them.',
          ],
          actions: [{
            label: on ? 'Flag the shelf' : 'Un-flag the shelf',
            value: 'go',
            tone: on ? 'primary' : 'danger',
          }],
        });
        if (choice !== 'go') return;
        handleUpdateCategory(rowId, { [field]: on });
        return;
      }
      if (field !== 'label') return;
      handleUpdateCategory(rowId, { label: value });
    }
  };

  // Add a line under L3 — family_id null mints a NEW family (+ its first
  // line); a real family_id adds another size to it.
  // Bug 3 (2026-07-21, Alex): family_id threaded through explicitly, never
  // inferred server-side.
  const handleAddLine = async (fields, family_id) => {
    const res = await fetch(`/api/catalog/categories/${activeL3Id}/lines`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ label: fields.label, family_id: family_id ?? undefined }),
    });
    if (!res.ok) { const e = await res.json(); throw new Error(e.error); }
    const newLine = await res.json();
    if (newLine._copied_from_family != null) {
      // ONE ITEM, ONE OWNER (INF-8): adding a size to a STANDARD item while
      // working in a project copied the whole item into the project, and the
      // size went into the copy. Reload (the copy brought every size with it),
      // move to the copy, and say so — the item on screen is not where it went.
      loadLines(activeL3Id);
      setSelectedFamilyId(newLine.family_id);
      setCopyNotice(`"${newLine.label}" is Standard, so it was copied into ${newLine.owner_project_code} with all its sizes, and your new size is in that copy. The Standard item is unchanged.`);
    } else {
      setLines(prev => [...prev, newLine]);
    }
    openForNewSize(newLine);
    refreshTree();
  };

  // Add a sub-category under L1/L2.
  const handleAddCategory = async (fields) => {
    const res = await fetch('/api/catalog/categories', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ parent_id: selectedCat._catId, code: fields.code, label: fields.label }),
    });
    if (!res.ok) { const e = await res.json(); throw new Error(e.error); }
    await refreshTree();
  };

  // The TOP grid's "+ Add" (gridOnAdd below). UX-40 — the top grid now shows
  // FAMILIES, so its own "+ Add" always mints a new one (family_id: null);
  // "another size in THIS family" moved to the Sizes card's own onAdd, which
  // passes selectedFamilyId explicitly instead of this reading it implicitly.
  const handleAdd = async (fields) => {
    if (isL3) return handleAddLine(fields, null);
    if (selectedCat) return handleAddCategory(fields);
  };

  // Delete: line (ref-count guarded server-side), category (empty-guard
  // server-side), or a whole multi-size FAMILY (note 7, Alex 2026-10-11: "Should
  // I be able to delete a full size stack at once in the main vs deleting all
  // sizes first"). The family delete is refused while any product uses it —
  // "it is correct that we should not be able to delete. But it must be clear to
  // the user" — so handleDeleteCheck names those products BEFORE the confirm,
  // and the server guard says the same words if anything slips past.
  const handleDelete = async (row) => {
    if (row._type === 'familyAgg') {
      const res = await fetch(`/api/catalog/families/${row.family_id}`, { method: 'DELETE' });
      if (!res.ok) { const e = await res.json().catch(() => ({ error: 'Delete failed' })); throw new Error(e.error || 'Delete failed'); }
      if (selectedFamilyId === row.family_id) setSelectedFamilyId(null);
      loadLines(activeL3Id);
      await refreshTree();
      return;
    }
    if (row._type === 'line') {
      const res = await fetch(`/api/catalog/lines/${row.id}`, { method: 'DELETE' });
      // Reject with the guard message so DataGrid shows it INLINE (no alert popup).
      if (!res.ok) { const e = await res.json().catch(() => ({ error: 'Delete failed' })); throw new Error(e.error || 'Delete failed'); }
      setLines(prev => prev.filter(l => l.id !== row.id));
      refreshTree();
    } else {
      const res = await fetch(`/api/catalog/categories/${row.id}`, { method: 'DELETE' });
      if (!res.ok) { const e = await res.json().catch(() => ({ error: 'Delete failed' })); throw new Error(e.error || 'Delete failed'); }
      await refreshTree();
    }
  };

  // INF-8 — Promote to Standard (internal only). A multi-size family row promotes
  // the whole item; any other row is one size (and its family, if that was the
  // project's too — server side). Held in a ref because the column definitions
  // are memoised, and a stale closure would reload the shelf you USED to be on.
  const authGroup = useAuthGroup();
  // INF-14 — which rows this screen may change, decided in can-change.js.
  const { lockOwned, canArrange, folderLock } = useCanChange();
  const promoteRef = useRef(null);
  promoteRef.current = async (row) => {
    const url = row._type === 'familyAgg'
      ? `/api/catalog/families/${row.family_id}/promote`
      : `/api/catalog/lines/${row.id}/promote`;
    const res = await fetch(url, { method: 'POST' });
    if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.error || 'Promote failed'); }
    loadLines(activeL3Id);
    refreshTree();
  };
  const onPromote = authGroup === 'internal' ? (row) => () => promoteRef.current(row) : null;

  // B4 — copy a catalog line (fast entry of size runs: copy, then edit size/price).
  const handleCopyLine = async (row) => {
    // A multi-size ITEM row copies the whole item, every size (UX-40's
    // copyFamily). It was wired server-side and never on screen — until INF-8
    // made "copy the item into my project" the way a project extends Standard.
    if (row._type === 'familyAgg') {
      const res = await fetch(`/api/catalog/families/${row.family_id}/copy`, { method: 'POST' });
      if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.error || 'Copy failed'); }
      const copy = await res.json();
      loadLines(activeL3Id);
      setSelectedFamilyId(copy.id);
      refreshTree();
      return;
    }
    if (row._type !== 'line') return;
    const res = await fetch(`/api/catalog/lines/${row.id}/copy`, { method: 'POST' });
    // Throws: DataGrid places a copy failure under the row you pressed it on. The real
    // case is "No free line letter under <shelf> — cannot copy", which is about THAT
    // shelf and reads as nonsense at the top of the page.
    if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.error || 'Copy failed'); }
    loadLines(activeL3Id);
    refreshTree();
  };

  // Copy in the SIZES card = the item's next size (Alex, 2026-10-11: copying a
  // Std RB size made a new item). The server keeps it in the family and gives it
  // the next free rung (8 after 7); a project copying a Standard item's size gets
  // the whole item copied first, same as + Add there. Same failure placement as
  // handleCopyLine: "already has every size on the list" belongs under the row.
  const handleCopySize = async (row) => {
    if (row._type !== 'line') return;
    const res = await fetch(`/api/catalog/lines/${row.id}/copy`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ as_size: true }),
    });
    if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.error || 'Copy failed'); }
    const copy = await res.json();
    loadLines(activeL3Id);
    openForNewSize(copy);
    if (copy.family_id !== row.family_id) {
      setSelectedFamilyId(copy.family_id);
      setCopyNotice(`"${copy.label}" is Standard, so it was copied into ${copy.owner_project_code} with all its sizes, and your new size is in that copy. The Standard item is unchanged.`);
    }
    refreshTree();
  };

  // Copy shelf (NIGHT_SHIFT.md item 2, 2026-07-19) — "make Mexico from US" in
  // ONE action: duplicate the currently-viewed L3 category (every line
  // beneath it) into a new sibling under the same L2, instead of one
  // copyLine per row. Prompts for the new code + label (per the brief).
  const handleCopyShelf = async () => {
    if (!isL3 || !activeL3Id) return;
    const code = window.prompt(`New shelf code (sibling of ${selectedCat?.code || ''}):`);
    if (!code) return;
    const label = window.prompt('New shelf label:', code);
    if (label == null) return;
    const res = await fetch(`/api/catalog/categories/${activeL3Id}/copy`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ code, label }),
    });
    if (!res.ok) { const e = await res.json().catch(() => ({ error: 'Copy failed' })); setActionError(e.error || 'Copy failed'); return; }
    await refreshTree();
  };

  // STANDARD (Alex 2026-07-02): guard on ✕ BEFORE the confirm. Categories: sync
  // count check. Lines: async where-used check mirroring the server deleteLine guard.
  const handleDeleteCheck = (row) => {
    // UX-40 — reached by the BULK-delete path (deleteBlockedIcon only hides
    // the single-row ✕; a multi-select can still include a family-summary row).
    if (row._type === 'familyAgg') {
      return fetch(`/api/catalog/families/${row.family_id}/usage`)
        .then(r => r.ok ? r.json() : null)
        .then(u => u?.message || null)
        .catch(() => null);
    }
    if (row._type === 'category') {
      if ((row.count || 0) > 0) return "Can't delete — empty it first";
      if ((row.counts?.total || 0) > 0) return "Can't delete — empty it first";
      return null;
    }
    if (row._type === 'line') {
      return fetch(`/api/catalog/lines/${row.id}/usage`)
        .then(r => r.ok ? r.json() : null)
        .then(u => {
          const usedBy = u?.used_by || [];
          if (usedBy.length) {
            const names = usedBy.slice(0, 5).map(p => p.code).join(', ');
            const more = usedBy.length > 5 ? `, +${usedBy.length - 5} more` : '';
            return `Line is used by ${usedBy.length} product(s) (${names}${more}) — remove those references before deleting.`;
          }
          // Legacy Parts-spike rows block server-side deleteLine too — warn
          // BEFORE the confirm instead of erroring after it.
          const legacy = u?.legacy_parts || [];
          if (legacy.length) {
            const names = legacy.slice(0, 3).map(p => p.display_name || p.part_ref).join(', ');
            return `Line is referenced by ${legacy.length} legacy part row(s) from the retired Parts spike (${names}) — those rows must be cleaned first.`;
          }
          return null;
        })
        .catch(() => null);
    }
    return null;
  };

  // Reorder (note 9, Alex 2026-10-11: "Looks like reorder is not working in the
  // catalog DB Family"). Two grids, two orders, and each index means its own grid:
  //   the TOP grid at a shelf — one row per FAMILY (UX-40) — moves the family,
  //     sizes and all. This used to look the row up in lineRows, where a
  //     multi-size row does not exist (silently ignored), and send a one-size
  //     row's FAMILY index to an endpoint that counted LINES (wrong place).
  //   the SIZES card moves a size within its family (index counts its sizes).
  //   a folder grid moves a category, unchanged.
  // A refusal says so (it used to `return` silently).
  const postMove = async (url, targetIndex) => {
    const res = await fetch(url, {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ toIndex: targetIndex }),
    });
    if (!res.ok) {
      const e = await res.json().catch(() => ({}));
      setActionError(e.error || `Reorder failed (${res.status})`);
      return false;
    }
    return true;
  };
  const handleReorder = async (rowId, targetIndex) => {
    if (isL3) {
      const row = gridRows.find(r => String(r.id) === String(rowId));
      if (!row || row.family_id == null) return;
      if (!(await postMove(`/api/catalog/families/${row.family_id}/move-order`, targetIndex))) return;
      loadLines(activeL3Id);
      await refreshTree();
      return;
    }
    const row = gridRows.find(r => String(r.id) === String(rowId));
    if (!row) return;
    if (!(await postMove(`/api/catalog/categories/${rowId}/move`, targetIndex))) return;
    await refreshTree();
  };
  const handleReorderSize = async (rowId, targetIndex) => {
    if (!(await postMove(`/api/catalog/lines/${rowId}/move-order`, targetIndex))) return;
    loadLines(activeL3Id);
    await refreshTree();
  };

  // Catalog #2 — re-parent. CatalogReparent now locks L1 in the UI (Cascade
  // consistency, 2026-07-08 — A75: cross-L1 disallowed), so `newL1Node` passed
  // in below is always the row's own L1 and this guard is a no-op from that UI.
  // Left in place as defense-in-depth (server still permits cross-L1 — see
  // FEEDBACK.md 2026-07-08) in case any other caller ever passes a different L1.
  // async now: the confirmation is rendered, not a blocking browser box, so both
  // callers await it (see the comment at line ~392 — this is a no-op today because
  // the UI locks L1, but it stays correct if that ever unlocks).
  const crossL1Ok = async (newL1Node) => {
    const curL1 = selectedAncestors.l1Id != null ? tree.find(n => n._catId === selectedAncestors.l1Id) : null;
    const oldCode = curL1 ? curL1.code : '?';
    const newCode = newL1Node ? newL1Node.code : '?';
    if (curL1 && newL1Node && curL1._catId !== newL1Node._catId) {
      const choice = await askConfirm({
        title: 'Moving across cost type',
        question: `${oldCode} → ${newCode} will RE-COST this item.`,
        impact: ['Its price is computed from its cost type, so the total will change.'],
        actions: [{ label: 'Move and re-cost', value: 'go', tone: 'danger' }],
      });
      return choice === 'go';
    }
    return true;
  };

  const handleReparentCategory = async (id, newParentId, newL1Node) => {
    if (!newParentId) return;
    if (!(await crossL1Ok(newL1Node))) return;
    const res = await fetch(`/api/catalog/categories/${id}/reparent`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ parent_id: newParentId }),
    });
    if (!res.ok) { const e = await res.json(); setActionError(e.error); return; }
    setEditRowId(null);
    await refreshTree();
  };

  const handleReparentLine = async (lineId, newCategoryId, newL1Node) => {
    if (!newCategoryId) return;
    if (!(await crossL1Ok(newL1Node))) return false;
    const res = await fetch(`/api/catalog/lines/${lineId}/reparent`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ category_id: newCategoryId }),
    });
    if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.error || 'Move failed'); }
    const moved = await res.json();
    setEditRowId(null);
    // Server now moves a whole sized family as a unit when the line has
    // siblings (REORG_MOVE_BUG.md) — a single-row filter would leave the
    // moved siblings stale in view, so reload the current L3 instead.
    loadLines(activeL3Id);
    await refreshTree();
    return moved;
  };

  // Move the WHOLE family — the family ROW moves, its lines follow because they
  // point at family_id (PATCH /api/catalog/families/:id, the same endpoint
  // saveFamilyLabel uses).
  //
  // This used to reparent one carrier LINE instead (2026-07-30 incident): with
  // more than one line the server delegated to reparentCatalogFamily and it
  // worked, but a ONE-line family took the line-level path, which finds-or-
  // CREATES a family at the destination. The old family kept its id, its label
  // and every product_comp pointing at it — and lost the only line those comps
  // resolve through, so $4,755.90 of product value silently priced $0. A family
  // of one and a family of many must behave identically; only this endpoint does
  // that, because the family_id never changes at all.
  const handleFamilyMove = async (famId, newL3Id, newL1Node) => {
    if (!newL3Id) return;
    if (!(await crossL1Ok(newL1Node))) return;
    const res = await fetch(`/api/catalog/families/${famId}`, {
      method: 'PATCH', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ category_id: newL3Id }),
    });
    // THROWS rather than reporting-and-returning: DataGrid's buffered Save reads a
    // quiet return as success. The one caller that is NOT a buffered save catches
    // this and puts it in the action Notice, exactly as before.
    if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.error || 'Move failed'); }
    setEditFamilyRowId(null);
    setEditRowId(null);
    loadLines(activeL3Id);
    await refreshTree();
    // The family_id is stable across the move, so follow it to its new L3.
    handleSelectNode(`c3-${newL3Id}|fam-${famId}`);
  };

  // Resolve an L1 code (e.g. "0-RM") to its catId from the loaded tree.
  const l1IdByCode = useCallback((code) => {
    const n = tree.find(t => t.code === code);
    return n ? n._catId : null;
  }, [tree]);

  const toggleStatus = (s) => setStatusFilter(prev => prev.includes(s) ? prev.filter(x => x !== s) : [...prev, s]);

  // --- Navigation -------------------------------------------------------------
  // App owns selectedNodeId; leaf ids are normalized HERE (family/line leaves
  // resolve to their L3 + an internal family/line selection) so App only ever
  // stores category ids — same behavior as before the extraction.
  const handleSelectNode = (id) => {
    if (id == null) { onSelectNode(null); return; }
    if (isFamilyNodeId(id)) {
      // B2 — selecting a FAMILY leaf: drill to its L3 grid, filtered to this family's
      // size rows (no single row to highlight/open — it's a group). Clear any
      // standalone-line selection explicitly — the [selectedNodeId] effect only
      // fires on an L3 CHANGE, so picking a family after a line leaf under the
      // SAME L3 would otherwise leave the stale line selected alongside it.
      const catNodeId = catNodeIdOfLeaf(id);
      const famId = Number(id.split('|')[1].slice(4));
      onSelectNode(catNodeId);
      setSelectedFamilyId(famId);
      setSelectedLineId(null);
      setSelectedRowId(null);
      revealFamilyRow.current = true;
      return;
    }
    if (isLineNodeId(id)) {
      // Selecting a standalone line leaf in the tree: drill to its L3 grid + highlight
      // the line. Clear any family selection explicitly (same-L3 cross-selection —
      // see note above).
      const catNodeId = catNodeIdOfLeaf(id);
      const lineId = Number(id.split('|')[1]);
      onSelectNode(catNodeId);
      setSelectedLineId(lineId);
      setSelectedRowId(lineId);
      setSelectedFamilyId(null);
      return;
    }
    // Plain category node — clear both leaf-selection kinds (same-L3 re-click case).
    setSelectedFamilyId(null);
    setSelectedLineId(null);
    setSelectedRowId(null);
    onSelectNode(id);
  };

  // Double-click on a category row = drill into it.
  // FIX (2026-07-20, found while verifying phase 3): this called
  // `setSelectedNodeId` — a name that stopped existing when CatalogView was
  // extracted from App.jsx (commit c23532b; selectedNodeId became a prop, and
  // every OTHER selection path in this file was updated to route through the
  // local `handleSelectNode` wrapper — this one call site was missed).
  // Category double-click-to-drill has been silently dead since that
  // extraction. Routes through handleSelectNode (not onSelectNode directly)
  // for the same reason every other selection here does: it also clears any
  // stale family/line leaf selection from whatever was open before.
  // familyAgg/line — 2026-08-23, moved here from single-click: L3 is now
  // editOnRowClick too (single click enters edit everywhere, no exceptions),
  // so a plain click can no longer double as open-the-Sizes-card /
  // open-ExplainerSubLine. Double-click is the one gesture left that still
  // reaches this grid unclaimed.
  // Open the Sizes card for one family. Extracted from handleGridRowDoubleClick
  // so the 'N sizes' cell and the (still-wired, still-dead-on-this-grid)
  // double-click run THE SAME code — two copies of 'select this family' would
  // drift, and this is exactly the pair that already drifted apart once.
  const openSizesForFamily = (row) => {
    if (!row || row._type !== 'familyAgg') return;
    // NOT a toggle. The old double-click closed the card when you hit the family
    // already open, but the card carries its own collapse chevron, so a second
    // way to close it is one the cell does not need to advertise — and a control
    // labelled "14 sizes" that sometimes HIDES the sizes is a worse promise than
    // one that always shows them. Re-opening the same family is idempotent.
    handleSelectNode(row.nodeId);
    revealFamilyRow.current = false;   // the click came FROM the row; scroll to the card instead
    setSelectedRowId(row.id);
    setSizesOpen(true);
    setScrollTarget({ to: 'sizes', t: Date.now() });
  };

  // The 3D cell's twin of openSizesForFamily: select the row's FAMILY (a one-size
  // family included) so the family's 3D Model card shows it, and bring the card
  // into view — it sits below the grid, so on a long shelf it would open off-screen
  // and the click would look like it did nothing.
  const openFamilyModel = (row) => {
    if (row?.family_id == null || activeL3Id == null) return;
    handleSelectNode(`c3-${activeL3Id}|fam-${row.family_id}`);
    revealFamilyRow.current = false;   // the click came FROM the row; scroll to the card instead
    setSelectedRowId(row.id);
    setScrollTarget({ to: 'model', t: Date.now() });
  };

  const handleGridRowDoubleClick = (row) => {
    if (row._type === 'category') { handleSelectNode(row.nodeId); return; }
    if (row._type === 'familyAgg') { openSizesForFamily(row); return; }
    if (row._type === 'line') {
      setSelectedLineId(row.id === selectedLineId ? null : row.id);
      setSelectedRowId(row.id === selectedRowId ? null : row.id);
    }
  };

  // --- Grid columns -----------------------------------------------------------
  // ONE skeleton at every level: # / Code (ro) / Label (edit) / type-col / actions.
  const catalogCols = useMemo(() => {
    if (!isL3) {
      // Folder levels — the SHARED folder grid (folder-cols.jsx; UI_VISION §10
      // "easiest win", forced by §8.0: the Onshape screen renders the same set).
      // Screen-specifics ride in as flags/slots: the L2-level re-parent column,
      // the root-only Cost Class chip, and the active-project Prj columns.
      const parentCol = selectedLevel === 2
        ? {
            // #2 — when viewing an L2's children (L3 rows), offer same-L1 re-parent:
            // move an L3 to a sibling L2 under the same L1. (L2 rows under an L1 get
            // no move column — an L2's L1 is fixed by Option A.)
            key: '_move', label: 'Parent', width: 200,
            render: row => {
              const inEdit = editRowId != null && String(row.id) === String(editRowId);
              return (
                <CatalogReparent
                  tree={tree} variant="l3"
                  l1Id={selectedAncestors.l1Id} l2Id={selectedAncestors.l2Id}
                  onMoveCategory={(newL2Id, newL1Node) => handleReparentCategory(row.id, newL2Id, newL1Node)}
                  disabled={!inEdit || !canArrange}
                />
              );
            },
          }
        : null;
      const extraCols = [];
      // Root grid only (L1 rows): read-only cost_class chip — makes the stable
      // engine classification visible now that L1 CODES are user-editable and no
      // longer imply cost behavior (Alex, 2026-07-02).
      // Flag (2026-07-29) — is_material / is_labor, edited HERE because they are
      // properties of the shelf. The line grid shows the same value read-only.
      // Only under an RM or HR L1: elsewhere the flag is meaningless and is not
      // what the picker guard checks. selectedLevel === 2 means these rows ARE
      // the L3 shelves.
      if (selectedLevel === 2) {
        const l1 = selectedAncestors.l1Id != null ? tree.find(n => n._catId === selectedAncestors.l1Id) : null;
        const cls = l1?.cost_class ?? null;
        if (cls === 'RM' || cls === 'HR') {
          const key = cls === 'RM' ? 'is_material' : 'is_labor';
          extraCols.push({
            key, label: 'Flag', width: 90, align: 'center', editType: 'checkbox', editable: true,
            render: row => <CheckboxCellRO value={row[key]} />,
          });
        }
      }
      if (selectedLevel == null) {
        extraCols.push({
          key: 'cost_class', label: 'Cost Class', width: 100, align: 'center',
          render: row => row.cost_class ? (
            <span style={{
              fontFamily: 'var(--ff-mono)', fontSize: 'var(--fs-mono)', fontWeight: 700,
              color: 'var(--th-navy)', background: 'var(--tg-chip-bg)',
              border: '1px solid var(--tg-chip-border)', borderRadius: 4,
              padding: '1px 6px',
            }}>
              {row.cost_class}
            </span>
          ) : null,
        });
      }
      // Overnight batch D (EXPERIMENTAL v1) — project usage columns, aggregated
      // from everything beneath this category (computed client-side in gridRows).
      if (activeProjectId && catalogUsage) {
        extraCols.push(
          {
            key: 'prj_qty', label: 'Prj Qty', width: 70, align: 'right',
            render: row => row.prj_qty ? <Text style={{ fontVariantNumeric: 'tabular-nums' }}>{formatQty(row.prj_qty)}</Text> : null,
          },
          {
            key: 'prj_cost', label: 'Prj Cost', width: 90, align: 'right',
            render: row => row.prj_cost ? <MoneyCell n={row.prj_cost} /> : null,
          },
        );
      }
      return makeFolderCols({
        seqKey: '_seq',
        parentCol,
        items: { value: row => row.count },
        statusMode: 'counts',
        extraCols,
      });
    }

    // L3 line grid — bespoke line columns (size/price/unit/…), built below.
    // Column order standard (Alex 2026-07-02, # dropped 2026-08-23 — display-only,
    // "if not required, remove it"): Parent · Tag · Description · …
    const cols = [
      {
        // THE PICTURE, FIRST (Alex, 2026-08-27: "move the image to left of each
        // row"). This grid is where families and their sizes actually live, so
        // it is the one that most needed a picture and — unlike the folder grids
        // — never had one at all.
        //
        // ⚠ A SIZE FALLS BACK TO ITS FAMILY, for both the stored picture and the
        // model. Six sizes of one elbow are six rows of the same part; making
        // each carry its own picture would mean six uploads to show one thing,
        // and the family ref is already what the aggregate row displays.
        // width/render bleed: same fix as folder-cols.jsx's own `_img` column —
        // 34 clipped a default-size (28px) ImageCell against the cell's own
        // padding (Alex, 2026-08-27: "the 3D pictures... truncated & offset").
        // This is a SEPARATE hand-rolled column (L3's bespoke cols, not
        // makeFolderCols), so it carried the same bug independently — found by
        // a column-width consistency sweep (COS-2), not by a second report.
        key: '_img', label: '', width: 48, align: 'center', resizable: false,
        render: row => {
          if (row._type === 'category') return null;
          const dbId = row._type === 'familyAgg'
            ? [`fam:${row.family_id}`]
            : [`line:${row.id}`, row.family_id ? `fam:${row.family_id}` : null];
          return (
            <div style={{ margin: -6, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <ImageCell
                dbId={dbId}
                onshapeRef={row._type === 'familyAgg'
                  ? row.family_onshape_ref
                  : (row.onshape_ref || row.family_onshape_ref)}
                size={40}
              />
            </div>
          );
        },
      },
      {
        // #2 — re-parent a line to any L3 (cross-L1 allowed; prompts on cost-type
        // change). Read mode = clickable crumbs (breadcrumb retirement). A row
        // of a MULTI-SIZE family never opens the editor: the family moves as one
        // unit from the family HEADER row (Alex, 2026-07-20).
        //
        // Which leaves ONE-line families as the only rows this editor opens on,
        // and moving the only line of a family IS moving the family — same
        // endpoint as the header row (2026-07-30). Routing it as a line instead
        // is what emptied family 489. Only a line with no family at all still
        // takes the line-level path.
        key: '_move', label: 'Parent', width: 140,
        render: (row, ctx) => {
          // UX-40 — the aggregate row (n>1) is now the ONLY row a multi-size
          // family's move control appears on (the wide "Family" header row it
          // used to live on is gone). It gets its OWN toggle (editFamilyRowId),
          // independent of the grid's shared editRowId — onEditRow refuses to
          // open the grid's normal edit mode for this row type (every other
          // column has nothing safe to save on a synthetic id), so Parent
          // can't piggyback on that toggle the way an ordinary row's can.
          if (row._type === 'familyAgg') {
            const inEdit = editFamilyRowId != null && String(row.family_id) === String(editFamilyRowId);
            return (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                <CatalogReparent
                  tree={tree} variant="line"
                  l1Id={selectedAncestors.l1Id} l2Id={selectedAncestors.l2Id}
                  onMoveLine={async (newL3Id, newL1Node) => {
                    // This row moves on pick by design — it has its own Done-moving
                    // toggle, not the grid's Save/Cancel — so it catches what the
                    // buffered path deliberately lets through.
                    try { await handleFamilyMove(row.family_id, newL3Id, newL1Node); }
                    catch (err) { setActionError(err.message); }
                  }}
                  disabled={!inEdit || !canArrange}
                  onNavigate={nodeId => handleSelectNode(nodeId)}
                  currentL3={selectedCat ? { id: selectedCat._catId, code: selectedCat.code } : null}
                />
                {canArrange && <button
                  onClick={e => {
                    e.stopPropagation();
                    setEditFamilyRowId(prev => String(prev) === String(row.family_id) ? null : row.family_id);
                  }}
                  title={inEdit ? 'Done moving' : 'Move this whole family'}
                  style={BTN_GLYPH}
                >{inEdit ? OK : EDIT}</button>}
              </span>
            );
          }
          const multiSize = row.family_id != null && (familyLineCount[row.family_id] || 0) > 1;
          const inEdit = !multiSize && editRowId != null && String(row.id) === String(editRowId);
          return (
            <CatalogReparent
              tree={tree} variant="line"
              l1Id={selectedAncestors.l1Id} l2Id={selectedAncestors.l2Id}
              // Buffered like every other cell: the move happens in handleSave
              // when Save is pressed, and Cancel drops it. `_move` is this
              // column's key, so DataGrid compares the pick against the row's
              // current parent and treats re-picking the same shelf as no change.
              onPick={newL3Id => ctx?.buffer?.(row.id, '_move', newL3Id)}
              disabled={!inEdit || !canArrange}
              onNavigate={nodeId => handleSelectNode(nodeId)}
              currentL3={selectedCat ? { id: selectedCat._catId, code: selectedCat.code } : null}
            />
          );
        },
      },
      {
        // Code: editable — data lines rename their l4 (Tag letter), guarded
        // server-side against code collisions. No addField: line adds auto-assign
        // their tag server-side, so no input in the ghost row.
        // width matched to every other Tag column app-wide (folder-cols.jsx,
        // ProjectsView) — was 60, a stray narrower than the others for no
        // content reason (a line's own code, e.g. "R2BPP", is if anything
        // longer than the codes elsewhere at 90). Found by a column-width
        // consistency sweep (COS-2).
        key: 'code', label: 'Tag', width: 90, editable: true, addField: false,
        render: row => <Text kind="code">{row.code}</Text>,
      },
      {
        // DM-5 (Alex, 2026-07-24: "I was expecting when changing a label, it
        // will change all of the same tag!"). The name is a property of the
        // FAMILY — "90° Elbow" does not vary by NPS — so this cell shows and
        // edits `catalog_families.label`, and every size row of the family
        // moves together. NO FALLBACK — `catalog_lines.label` is dropped (Alex,
        // 2026-07-28: "row.label should not exist = Delete. Label comes from the
        // family."). The `?? row.label` that used to sit here is exactly what let
        // one family show three different names on the damper shelf: when the
        // family name did not resolve, every row printed its own stored copy and
        // the disagreement looked like data. There is nothing left to fall back
        // to, so that screenshot is now impossible rather than merely corrected.
        key: 'label', label: 'Label', width: 230, editable: true, addField: true,
        editValue: row => row.label ?? '',
        // INF-8 — a project's own row says so IN WORDS (Alex is colour-blind):
        // "<project> only". Standard rows say nothing; they are the default.
        render: row => <OwnerLabel label={row.label} ownerOnly={row._ownerOnly} onPromote={onPromote?.(row)} />,
      },
    ];

    // Current L1 node's cost_class on the path to this L3 (e.g. 'RM' / 'HR' / 'MF') —
    // drives the is_material/is_labor Flag + FabProductCosting columns below.
    // CostClass (Alex, 2026-07-02): classified via cost_class (rename-immune), never
    // the L1 code itself — Alex just renamed all six L1 codes and the code-keyed
    // version silently broke.
    const curL1Node = selectedAncestors.l1Id != null ? tree.find(n => n._catId === selectedAncestors.l1Id) : null;
    const curL1Class = curL1Node ? curL1Node.cost_class : null;
    // FabProductCosting trade dropdown (Alex, 2026-07-02): trades table ids + blank.
    const tradeSelectOptions = [{ value: '', label: '—' }, ...tradeOptions.map(t => ({ value: t.id, label: t.id }))];
    // The RM Source candidates for a material NAME — the flagged raw-material rows
    // Line columns (type-col group): size / price / unit / mass / hrs / material / status.
    {
      cols.push(
        {
          // Size-list picker (Spike, 2026-07-13): Size Unit → Size cascade, gated on
          // inEdit like the Parent col above — custom render, not editable/editType.
          // Read mode: the resolved look for a standard pick (size_id in the cache),
          // else the raw free-text `size`. Edit mode: Level 1 (Size Unit) is LOCKED
          // once the family already has a size_list (reclassifying is deferred to a
          // future Maintenance panel); Level 2 (Size) is a CascadeDropdown for a
          // standard unit, or a free-text input (suggesting the family's own past
          // custom values) for 'custom'.
          // 140, not 260 (Alex, 2026-07-27: "column Size be narrower, it is too
          // wide"). Read mode shows a short label ('6"', 'NPS 4'); only the edit-mode
          // cascade needs room, and it wraps rather than overflowing.
          key: 'size', label: 'Size', width: 80,
          render: (row, ctx) => {
            const inEdit = editRowId != null && String(row.id) === String(editRowId);
            const lockedUnit = row.family_id != null ? familySizeListById[row.family_id] : null;

            if (!inEdit) {
              let display = row.size || '—';
              if (row.size_list && row.size_list !== 'custom' && row.size_id) {
                const opt = (sizeListValuesCache[row.size_list] || []).find(o => String(o.value) === String(row.size_id));
                if (opt) display = opt.label;
              }
              return <span>{display}</span>;
            }

            // Staged like every other cell (Alex, 2026-07-19: "it's better to have
            // all the same"). This picker used to write on pick while the rest of the
            // row waited for Save — so Cancel could not undo a size change. Reads go
            // through the buffer too, otherwise the control would show the saved value
            // while holding a different staged one.
            const bufd = ctx?.buffered || {};
            const bget = (k, fb) => (k in bufd ? bufd[k] : fb);
            const stage = fields => {
              for (const [k, v] of Object.entries(fields)) ctx?.buffer?.(row.id, k, v);
            };
            // A row can carry a FREE-TEXT size with no size_list at all — 71 of them
            // (Alex, 2026-07-28: "I could not modify the label to include the size and
            // delete the size"). Its unit is null, so effectiveUnit was '' and the text
            // box never rendered: the size was ON the row, visible in read mode, and
            // completely absent from the editor. Clearing it meant knowing to pick
            // "#NA — no size" from a dropdown that showed "— pick —". An existing value
            // the editor cannot show is the same bug as a cell that will not open.
            // A stored free-text size IS a custom size, so say so.
            const effectiveUnit = lockedUnit || bget('size_list', row.size_list) || '';
            // NOT-YET-CHOSEN vs EXPLICITLY-NO-SIZE (Alex, 2026-07-21: "I cannot
            // edit Size in the row"). The cell was never broken — it opened
            // reading "#NA — no size" on a brand-new row, which STATES a
            // decision nobody made, and the box you type the size into does not
            // exist until a unit is picked. So it read as dead.
            //
            // The cause: `#NA` was coded as '', and CascadeDropdown resolves a
            // null value to '' too — so "never chosen" (size_list IS NULL) and
            // "chosen: no size" (size_list = '') selected the SAME option and
            // were indistinguishable. #NA now has its own sentinel, leaving ''
            // /null free to mean "nothing picked yet" and show a prompt.
            // This is the three-state empty rule (NIGHT_SHIFT item 6) applied
            // one level up: #NA must be a state you CHOSE, never the state you
            // start in.
            const NA_UNIT = '__na__';
            const rawUnit = lockedUnit ?? bget('size_list', row.size_list);
            // '' and 'unique' both mean "no size axis" — one is the UI's blank, the other
            // the stored sentinel. Both must READ as #NA, or the cell shows nothing at all.
            const unitValue = rawUnit == null ? null : ((rawUnit === '' || rawUnit === 'unique') ? NA_UNIT : rawUnit);
            const unitOptions = [
              ...sizeUnitNames.map(n => ({ code: n, label: n })),
              // "no size" must be PICKABLE, not just a rendered #NA (Alex, 2026-07-19;
              // already logged in STATUS "Size #NA not available"). A stock row has no
              // size ladder at all, and until now the cell offered no way to say so.
              // `display` overrides the mono CODE column, so the internal
              // sentinel never reaches the screen — it would otherwise render as
              // "__na__ | #NA — no size". Closed-button text falls back
              // display -> code -> label, so this also makes the picked state
              // read "#NA" rather than the sentinel.
              { code: NA_UNIT, display: '#NA', label: 'no size' },
            ];
            const customSuggestions = row.family_id != null
              ? [...new Set(lines
                  .filter(l => l.family_id === row.family_id && (l.size_list === 'custom' || l.size_list == null) && l.size)
                  .map(l => l.size))]
              : [];
            const suggestListId = `size-suggest-${row.id}`;

            return (
              <SizeEditCell
                lockedUnit={lockedUnit}
                unitValue={unitValue}
                unitOptions={unitOptions}
                naUnit={NA_UNIT}
                effectiveUnit={effectiveUnit}
                sizeIdValue={bget('size_id', row.size_id) || ''}
                sizeOptions={(sizeListValuesCache[effectiveUnit] || []).map(o => ({ code: o.value, display: o.label, label: '' }))}
                customValue={bget('size', row.size)}
                customSuggestions={customSuggestions}
                suggestListId={suggestListId}
                stage={stage}
                fetchSizeListValues={fetchSizeListValues}
                startTyping={pendingNewSize?.rowId === row.id}
                guess={pendingNewSize?.rowId === row.id ? pendingNewSize.guess : ''}
                typedValue={bget('new_size_look', pendingNewSize?.rowId === row.id ? pendingNewSize.guess : '')}
              />
            );
          },
        },
        // ONE price, one path (2026-08-18). This cell used to MUTE itself at 50%
        // opacity whenever price_method='formula', with a tooltip saying "the Parts
        // screen uses the Recipe instead" — true until 2026-08-17, when PART_SELECT
        // and the parts table were deleted. After that it greyed out the unit cost
        // on all 66 formula rows and pointed at a screen that no longer existed.
        //
        // Formula-backed (NIGHT_SHIFT.md item 4, 2026-07-19): editing/staging keys
        // off `price_expr` (a formula, evaluated server-side — see updateLine),
        // but the #NA gate (naCol/NA_COLUMNS_BY_CLASS) still means "does the
        // engine read the flat price?", so naKey pins it back to 'price'. A row
        // with no expression yet renders identical to a plain MoneyCell.
        naCol(formulaCol({ key: 'price_expr', valueKey: 'price', label: 'Unit Cost', width: 100, kind: 'money', naKey: 'price' }), curL1Class),
        { key: 'unit', label: 'Unit', width: 70, editType: 'select', options: ['', 'each', 'set', 'm2', 'kg', 'm', 'm3', 'hr'] },
        { key: 'kg', label: 'Mass', width: 60, align: 'right', editable: true, render: row => row.kg != null ? row.kg : '' },
        naCol({ key: 'hrs', label: 'Hrs', width: 50, align: 'right', editable: true }, curL1Class),
        // Productivity (NIGHT_SHIFT.md item 5 — Alex's call, 2026-07-19): the
        // crew's pace, carried by the LABOUR LINE, DIVIDING the hours.
        // Productivity = output / input. "Need 2 hours for a normalised output
        // of 1 h" = 50% → 1/0.5 = 2 hours. "In a 480-minute day only 350
        // productive minutes" = 350/480 = 72.9% → hours × 1.371.
        //
        // ⚠ DIRECTION — BELOW 100% is SLOWER and therefore DEARER, which is the
        // opposite of how a percentage usually reads and exactly what cost Alex
        // time on 2026-07-19. It must never render as a bare number: the `suffix`
        // says "of standard" in the cell itself and the tooltip spells it out.
        // (The Net→Gross cell it used to be contrasted against was removed with
        // catalog_fabrication on 2026-08-18 — waste now lives inside the
        // fabrication primitive, see PATTERN_fabrication_primitive.md.)
        //
        // Formula-backed: "350/480" is precisely what Alex wants to type AND
        // keep, so it reuses labor_productivity_expr through the identical
        // evalFormula path (item 4) — not a parallel mechanism.
        // HR-only; see NA_COLUMNS_BY_CLASS above.
        naCol(formulaCol({
          key: 'labor_productivity_expr', valueKey: 'labor_productivity', naKey: 'labor_productivity',
          label: 'Productivity', width: 80, kind: 'percent', suffix: 'of standard',
          title: row => {
            const v = row.labor_productivity != null ? Number(row.labor_productivity) : 1;
            const pct = Math.round(v * 1000) / 10;
            const mult = Math.round((1 / (v > 0 ? v : 1)) * 1000) / 1000;
            return `Crew pace — DIVIDES the hours: hours ÷ ${pct}% = hours × ${mult}. 100% = standard; BELOW 100% is slower and therefore DEARER (50% = double the hours). Same direction as FabRate, the OPPOSITE of Efficiency.`;
          },
        }), curL1Class),
        // Material — WHAT the item is made of (catalog_families.material_id), now
        // editable on EVERY class. Alex, 2026-07-30: "Is there a way to select the
        // material. And then we choose the RM source level — but not the material."
        //
        // It used to be editable on RM only; everywhere else it was read-only and
        // set as a SIDE EFFECT of picking an RM Source, because on 2026-07-19
        // letting both be edited independently let them disagree. That rule was not
        // broken — it was UNREACHABLE for 59 families (44 MF · 11 PE · 4 HR) that
        // carry a material and have NO catalog_fabrication row, so no RM Source
        // exists to set it through. And it forced a PROCUREMENT decision (where to
        // buy) in order to record a DESIGN fact (what it is).
        //
        // The two can no longer disagree BY CONSTRUCTION, without syncing: the
        // material is picked here, and the RM Source cascade below is FILTERED to
        // rows of that material, so it can only offer places to buy that thing.
        // Nothing is kept in step because nothing can drift apart.
        //
        // On an RM stock row the material is still the row's OWN identity rather
        // than a reference — same control, same direct edit, unchanged.
        naCol({
          key: 'material', label: 'Material', width: curL1Class === 'RM' ? 120 : 220,
          render: (row, ctx) => {
            const inEdit = editRowId != null && String(row.id) === String(editRowId);
            if (!inEdit) {
              return row.material
                ? <Text>{row.material}</Text>
                : <Text kind="empty" />;
            }
            // Staged through ctx.buffer like every other cell, so Cancel undoes it
            // (DataGrid's contract for a column that renders its own editor).
            const bufd = ctx?.buffered || {};
            const cur = 'material' in bufd ? bufd.material : (row.material ?? '');
            return (
              <SelectCell
                value={cur}
                options={['', ...materialOptions]}
                title="What this item is MADE OF."
                onChange={name => ctx?.buffer?.(row.id, 'material', name)}
              />
            );
          },
        }, curL1Class),
        // The Flag column is GONE from this grid (Alex, 2026-07-29: "the material
        // flag is still at the family level"). is_material / is_labor belong to
        // the SHELF, so repeating them on every family and size row under it said
        // the same thing many times and invited an edit that could not work.
        // They are shown — and edited — once, on the L3 row in the category grid,
        // beside cost_class, which is the other shelf-level property.
        {
          key: 'status', label: 'Status', width: 150, editType: 'select',
          options: STATUSES.map(s => ({ value: s, label: STATUS_LABEL[s] })),
          render: row => <StatusCell value={row.status} />,
        },
        // Phase 1a — editable Onshape link (did/wid/eid[/pid]); empty clears the link.
        // Edit mode: plain text input (TextCell, via editable:true). Display: ↗ icon +
        // muted shortened ref (OnshapeRefCell, shared with the product-info grid).
        // Server validates on save (catalog-db.js updateLine).
        //
        // Family-level Onshape linking (2026-07-03): when the line's FAMILY carries
        // an onshape_ref, DISPLAY mode shows a compact read-only ↗ link to the
        // derived per-size configured URL (_onshape_url, server-side string
        // derivation — see catalog-db.js enrichLinesWithFamilyOnshape) instead of the
        // plain-line OnshapeRefCell. The ref/config base themselves are edited once on
        // the family (FamilyOnshapePanel) — this column stays editable:true like
        // before (DataGrid has no per-row editable gate) so a line can still carry
        // its own independent onshape_ref if ever needed; the family link always
        // wins for DISPLAY regardless of what's in the line's own field.
        {
          key: 'onshape_ref', label: 'Onshape', width: 80, editable: true,
          render: row => row.family_onshape_ref ? (
            row._onshape_url ? (
              <a
                href={row._onshape_url}
                target="_blank" rel="noopener noreferrer"
                title="Open in Onshape at this size"
                aria-label="Open in Onshape at this size"
                onClick={e => e.stopPropagation()}
                style={{ display: 'inline-flex', alignItems: 'center', color: NAVY, fontSize: 'var(--fs-small)' }}
              >{LINK}</a>
            ) : (
              <Text kind="empty" title="Size doesn't map to a valid Onshape option id" />
            )
          ) : <OnshapeRefCell value={row.onshape_ref} />,
        },
        // Overnight batch D (EXPERIMENTAL v1) — project usage columns; hidden entirely
        // when no project is selected (today's layout unchanged).
        ...(activeProjectId && catalogUsage ? [
          {
            // F5 (Alex 2026-07-03): line rows have a single unit — show it muted
            // beside the qty (e.g. "2,666 kg"). Category rollups (below) mix units
            // across lines, so they keep the bare number.
            key: 'prj_qty', label: 'Prj Qty', width: 90, align: 'right',
            render: row => row.prj_qty ? (
              <Text style={{ fontVariantNumeric: 'tabular-nums' }}>
                {formatQty(row.prj_qty)}
                {row.unit ? <span style={{ color: 'var(--th-text-secondary)' }}> {row.unit}</span> : null}
              </Text>
            ) : null,
          },
          {
            key: 'prj_cost', label: 'Prj Cost', width: 90, align: 'right',
            render: row => row.prj_cost ? <MoneyCell n={row.prj_cost} /> : null,
          },
        ] : []),
      );
    }
    // The fabrication-only columns (QtyDrv, FabRate, Labour, RM Source, Unit Conv,
    // Net→Gross) were removed with catalog_fabrication on 2026-08-18, and the
    // MF-vs-rest split that positioned them went with them. A fabrication
    // primitive is a PRODUCT now: its material and labour are components, priced
    // on the Product screen, so the catalog line carries no fabrication recipe.
    //
    // UX-40 — the only three columns a family-summary row (n>1) renders
    // differently from an ordinary line row; see the wrapper functions above.
    // `size` takes the extra argument; the other two ignore it. Passing the
    // opener here rather than reaching for a ref keeps aggSizeCol a pure
    // function of its inputs, like the two beside it.
    const AGG_COL_WRAPPERS = { size: aggSizeCol, price_expr: aggPriceCol, onshape_ref: aggOnshapeCol };
    const wrapped = cols.map(c => AGG_COL_WRAPPERS[c.key] ? AGG_COL_WRAPPERS[c.key](c, openSizesForFamily, openFamilyModel) : c);
    if (!isL3) return wrapped;
    // Note 8 — WHERE each field is edited follows WHOSE field it is. Tag and Label
    // belong to the FAMILY: editable on the family row (and on a one-size family's
    // only row, which stands for it), read-only on a size row in the Sizes card.
    // Every other column is size-dependent: editable on a size row, never on the
    // family row, which has no single size to write to.
    const isSizeOfMany = row => row._type === 'line' && (familyLineCount[row.family_id] || 0) > 1;
    return wrapped.map(c => {
      if (c.key === 'code' || c.key === 'label') return { ...c, editable: row => !isSizeOfMany(row) };
      if (!c.editable && !c.editType) return c;
      const out = { ...c };
      if (c.editable) out.editable = row => row._type !== 'familyAgg';
      else out.editType = row => (row._type === 'familyAgg' ? undefined : (typeof c.editType === 'function' ? c.editType(row) : c.editType));
      return out;
    });
  }, [isL3, selectedLevel, familySizeListById, familyLineCount, selectedCat, sizeUnitNames, sizeListValuesCache, lines, pendingNewSize, materialOptions, tree, editRowId, editFamilyRowId, selectedAncestors, tradeOptions, activeProjectId, catalogUsage, canArrange]); // eslint-disable-line react-hooks/exhaustive-deps

  // --- Grid rows --------------------------------------------------------------
  // lineRows: every line under the selected L3, one row per SIZE, always
  // unfiltered (UX-40 moved "selectedFamilyId narrows this" to the dedicated
  // Sizes card below — see sizeCardRows). Backs the Sizes card AND every
  // save/reorder row lookup, so it has to carry every size regardless of
  // what's selected in the top (family) grid.
  const lineRows = useMemo(() => {
    if (isL3) {
      const shownLines = projectOnly ? lines.filter(l => l.owner_project_id != null && String(l.owner_project_id) === String(workingIn)) : lines;
      return shownLines.map((line, i) => ({
        id: line.id,
        nodeId: `c3-${line.category_id}|${line.id}`,
        _type: 'line',
        _seq: i + 1,
        // The Parent column's ORIGINAL value. DataGrid's commitRow only saves a
        // buffered cell whose value differs from the row's own, so without this
        // every pick would read as a change — including re-picking the shelf the
        // item is already on, which is exactly the pointless reparent handleL3
        // already refuses to fire.
        _move: line.category_id,
        code: line.code,
        l1: line.l1,
        l3: line.l3,
        label: line.label,
        size: line.size,
        size_id: line.size_id,
        size_list: line.size_list,
        price: line.price,
        price_expr: line.price_expr,
        unit: line.unit,
        kg: line.kg,
        hrs: line.hrs,
        labor_productivity: line.labor_productivity,
        labor_productivity_expr: line.labor_productivity_expr,
        material: line.material,
        // The POINTER, not just the name (NIGHT_SHIFT.md item 1) — _rm_candidates
        // is keyed on it server-side, and the whitelist is why it has to be here.
        material_id: line.material_id,
        status: line.status,
        price_method: line.price_method,
        onshape_ref: line.onshape_ref,
        // Family-level Onshape linking (2026-07-03) — family_id already carried below;
        // family_onshape_ref/_onshape_url are server-derived (catalog-db.js
        // enrichLinesWithFamilyOnshape) so a sized line under a linked family gets a
        // compact ↗ link to its OWN configured size, no client-side derivation.
        family_id: line.family_id,
        family_onshape_ref: line.family_onshape_ref,
        _onshape_url: line._onshape_url,
        is_material: line.is_material,
        is_labor: line.is_labor,
        // INF-8 — the size's own owner, else its family's (a one-size family row
        // stands for the family). Null = Standard.
        owner_project_id: line.owner_project_id,
        _ownerOnly: line.owner_project_code || line.family_owner_project_code || null,
        _familyOwnerOnly: line.family_owner_project_code || null,
        // ⚠ This mapping is an explicit WHITELIST — a field absent here is
        // `undefined` on the grid row no matter what the API sent, and a column
        // reading it silently renders its empty state. That is exactly how
        // FabLabour shipped showing "none" on every row in read mode while its
        // edit-mode picker worked: `labor_line` was never copied. Add the
        // field here whenever a column starts reading a new one.
        // Overnight batch D — byLine already carries BOTH plain-ref and sized-ref
        // (via resolved_line_id) contributions keyed on the literal catalog line id.
        prj_qty: catalogUsage?.byLine?.[String(line.id)]?.qty ?? 0,
        prj_cost: catalogUsage?.byLine?.[String(line.id)]?.cost ?? 0,
      }));
    }
    // Folder grid: children of selectedCat, or roots when nothing selected.
    const kids = (selectedCat ? (selectedCat.children || []).filter(c => !c.isLine) : tree)
      .filter(c => !shownIds || shownIds.has(c.id));
    return kids.map((c, i) => {
      const usage = catalogUsage ? sumCatalogUsageForCategory(c, catalogUsage) : null;
      return {
        id: c._catId,
        nodeId: c.id,
        _type: 'category',
        _seq: i + 1,
        code: c.code,
        label: c.label,
        count: (c.children || []).length,
        counts: c.counts || {},
        cost_class: c.cost_class ?? null,
        // The Flag column reads these. gridRows is an explicit WHITELIST — a
        // field the server sends but this object omits simply never reaches the
        // grid, which is how four fab fields once rendered as $0.00 with a green
        // build and a correct API response.
        is_material: c.is_material ?? 0,
        is_labor: c.is_labor ?? 0,
        prj_qty: usage?.qty ?? 0,
        prj_cost: usage?.cost ?? 0,
      };
    });
  }, [isL3, lines, selectedCat, tree, catalogUsage, projectOnly, workingIn, shownIds]);

  // gridRows — the TOP grid. Unchanged at folder levels (one row per child).
  // At L3 (UX-40): one row per FAMILY, not per size. A family with exactly
  // one line passes through as its ordinary line row; a family with more than
  // one collapses into a single read-only summary row (see the aggCol
  // wrappers above) that opens the Sizes card on click. _seq is renumbered
  // after grouping, since collapsing rows shifts every count after the first.
  const gridRows = useMemo(() => {
    if (!isL3) return lineRows;
    const byFam = new Map(); // family_id -> that family's line rows, in order
    const order = []; // interleaves standalone lines and families in first-seen order
    for (const row of lineRows) {
      if (row.family_id == null) { order.push({ kind: 'line', row }); continue; }
      if (!byFam.has(row.family_id)) { byFam.set(row.family_id, []); order.push({ kind: 'fam', family_id: row.family_id }); }
      byFam.get(row.family_id).push(row);
    }
    const out = [];
    for (const entry of order) {
      if (entry.kind === 'line') { out.push(entry.row); continue; }
      const famLines = byFam.get(entry.family_id);
      if (famLines.length === 1) { out.push(famLines[0]); continue; }
      const first = famLines[0];
      out.push({
        id: `fam-${entry.family_id}`,
        nodeId: `c3-${activeL3Id}|fam-${entry.family_id}`,
        _type: 'familyAgg',
        family_id: entry.family_id,
        // The Tag (l4) belongs to the FAMILY, not one size — every size row
        // shares it (Alex, 2026-08-21: "you can still keep the tag in the
        // family because it's the same tag for all the sizes" — confirmed;
        // the tree already made this same assumption for its own family
        // leaf, catalog-db.js listCatalogTree: `code: first.code`).
        code: first.code,
        l3: first.l3,
        label: first.label,
        _ownerOnly: first._familyOwnerOnly,
        // One item, one owner (INF-11): the family's owner is every size's owner.
        owner_project_id: first.owner_project_id,
        sizeCount: famLines.length,
        _priceRange: numRange(famLines.map(l => l.price)),
        unit: first.unit,
        kg: null,
        hrs: null,
        labor_productivity: null,
        material: first.material,
        material_id: first.material_id,
        status: familyAggStatus(famLines),
        onshape_ref: null,
        family_onshape_ref: first.family_onshape_ref,
        _onshape_url: null,
        prj_qty: famLines.reduce((s, l) => s + (l.prj_qty || 0), 0),
        prj_cost: Math.round(famLines.reduce((s, l) => s + (l.prj_cost || 0), 0) * 100) / 100,
      });
    }
    return out.map((r, i) => ({ ...r, _seq: i + 1 }));
  }, [isL3, lineRows, activeL3Id]);

  // Reveal a family picked from outside the grid (see revealFamilyRow): highlight
  // its row — the family row, or the one size of a one-size family — and scroll it
  // into view once the shelf has loaded. Runs once per pick, never on a row click.
  useEffect(() => {
    if (!revealFamilyRow.current || !isL3 || selectedFamilyId == null) return;
    const row = gridRows.find(r => r.family_id === selectedFamilyId);
    if (!row) return;                       // rows not loaded yet — try again when they are
    revealFamilyRow.current = false;
    setSelectedRowId(row.id);
    // INSTANT, not smooth: a row sits inside the grid's own (non-scrolling) overflow
    // boxes, and Chromium's smooth scrollIntoView silently does nothing through them
    // — measured 2026-09-30: smooth left the pane at 0, instant reached the row.
    requestAnimationFrame(() => {
      document.querySelector(`tr[data-row-id="${CSS.escape(String(row.id))}"]`)
        ?.scrollIntoView({ block: 'center' });
    });
  }, [isL3, selectedFamilyId, gridRows]);

  // The line beside Save when the typed size is NOT on the list yet — the one
  // confirmation for extending a list. Lists are shared, so it says so.
  const sizeEditWarnings = (row, buffered) => {
    const typed = String(buffered.new_size_look ?? '').trim();
    const list = familySizeListById[row.family_id] || row.size_list;
    if (!typed || !list) return [];
    const norm = (x) => String(x).replace(/\s+/g, '').toLowerCase();
    if ((sizeListValuesCache[list] || []).some(o => norm(o.label) === norm(typed))) return [];
    return [`New size: saving adds ${typed} to the ${list} list, for every item that uses it.`];
  };

  // sizeCardRows — the Sizes card's content: one multi-size family's own
  // lines (client-side filter off lineRows, which is always unfiltered).
  // Empty (and the card hidden) whenever nothing is selected or the selected
  // family has only one line — BACKLOG UX-40(b): "at n=1 the card should not
  // appear."
  const sizeCardRows = useMemo(() => {
    if (!isL3 || selectedFamilyId == null) return [];
    return lineRows.filter(r => r.family_id === selectedFamilyId);
  }, [isL3, lineRows, selectedFamilyId]);

  // onAdd available at every folder level (sub-category) and L3 (line). Not on the
  // line leaf view. (Adding under root would need an L0 — keep root read-only-ish:
  // root shows L1s which are fixed namespaces, so no add there.)
  const gridOnAdd = isL3 ? handleAdd : (selectedCat ? handleAdd : undefined);

  // Status chips EMPHASISE, they no longer hide (Alex, 2026-07-18). A row whose
  // status isn't selected is MUTED, never removed. Muting is by OPACITY, never
  // colour — Alex is colour-blind and the codebase convention is "shape =
  // meaning; no color signal" (TreeNav's status glyph). This subsumes the old
  // deprecated-only rule: deprecated is off in DEFAULT_STATUS_FILTER, so a
  // deprecated row mutes by the same path.
  // What the assistant wrote to this shelf (assistant-writes.js): named in the notice above
  // the grid, and those rows shaded. A collapsed family row is shaded when any of its sizes
  // or the family itself changed.
  const reloadShelf = useCallback(() => loadLines(activeL3Id), [loadLines, activeL3Id]);
  const assistantWrite = useAssistantWrite({ kind: 'catalog', id: activeL3Id, reload: reloadShelf, refreshKey: lines });
  const assistantChanged = useMemo(() => {
    const c = assistantWrite.write?.changes;
    return {
      lines: new Set((c?.lines || []).map(l => l.id)),
      families: new Set([...(c?.lines || []).map(l => l.family_id), ...(c?.families || []).map(f => f.id)]),
    };
  }, [assistantWrite.write]);
  const changedByAssistant = row => (row._type === 'familyAgg'
    ? assistantChanged.families.has(row.family_id ?? Number(String(row.id).replace('fam-', '')))
    : assistantChanged.lines.has(row.id));

  const gridRowStyle = isL3
    ? (row => ({
        ...(statusFilter.includes(row.status) ? null : { opacity: 0.4 }),
        ...(changedByAssistant(row) ? { background: 'var(--th-highlight-bg)' } : null),
      }))
    : undefined;

  // G2 (batch G) — with an active project, the header "Project total" text is
  // replaced by a footer row: label cell 'Project total' + summed Prj Qty (bare
  // number) + Prj Cost for the rows CURRENTLY DISPLAYED (line grid: sum of visible
  // lines; category grid: sum of the displayed category rollups). Category grids
  // additionally aggregate Items (sum of counts) + StatusCounts (sum of the
  // counts objects) across the displayed rows, rendered with the same StatusCounts
  // component used on the rows themselves.
  const catalogFooterRow = useMemo(() => {
    if (!activeProjectId || !catalogUsage || !gridRows.length) return null;
    const totalQty = gridRows.reduce((s, r) => s + (r.prj_qty || 0), 0);
    const totalCost = Math.round(gridRows.reduce((s, r) => s + (r.prj_cost || 0), 0) * 100) / 100;
    const labelCell = <strong style={{ color: 'var(--th-navy)', fontSize: 'var(--fs-small)' }}>Project total</strong>;
    const qtyCell = totalQty ? (
      <span style={{ fontSize: 'var(--fs-small)', fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>{formatQty(totalQty)}</span>
    ) : null;
    const costCell = totalCost ? <MoneyCell n={totalCost} bold /> : null;
    if (isL3) {
      return { label: labelCell, prj_qty: qtyCell, prj_cost: costCell };
    }
    // Category grid — also aggregate Items + Status across the displayed rows.
    const totalItems = gridRows.reduce((s, r) => s + (r.count || 0), 0);
    const aggCounts = {};
    for (const r of gridRows) {
      for (const [k, v] of Object.entries(r.counts || {})) aggCounts[k] = (aggCounts[k] || 0) + v;
    }
    return {
      label: labelCell,
      // Keyed '_items' — the shared folder grid's Items column (folder-cols.jsx).
      _items: <Text kind="note" bold>{totalItems}</Text>,
      status: <StatusCounts counts={aggCounts} />,
      prj_qty: qtyCell,
      prj_cost: costCell,
    };
  }, [activeProjectId, catalogUsage, gridRows, isL3]);

  // --- Category header row (Alex, 2026-07-20: "go") ---------------------------
  // L1/L2/L3 get their own header row, which is what lets the breadcrumb retire
  // on this screen entirely — every selectable category has a row of its own.
  //
  // UX-40 follow-up (Alex, 2026-08-21, on selecting a multi-size family: "the
  // top header is changing to 'Family'... that's not correct, because just
  // underneath you are the family list"). This row used to stand down for a
  // selected FAMILY (the old "§5 WIDE ROW" replaced it, mirroring how a LINE
  // selection was ALSO once thought to need that — UX-36 already settled that
  // a line selection does not change which CATEGORY you are in and the row
  // must stay). A family selection doesn't change the category either, and now
  // that its own Tag/Label/Status already show as the grid row you clicked —
  // that wide row was pure duplication with no data of its own — so this row
  // no longer stands down for ANYTHING selected beneath it, matching the line
  // case it was always inconsistent with.
  const [editCatRowId, setEditCatRowId] = useState(null);

  const categoryHeaderRow = useMemo(() => {
    if (!selectedCat) return null;
    // Items: for L1/L2 this is "how many sub-categories" (what the grid below
    // shows); for L3 it's "how many lines" — the tree node's own `.children`
    // doesn't carry lines (those come from the separate `lines` fetch).
    const itemCount = isL3 ? lines.length : (selectedCat.children || []).filter(c => !c.isLine).length;
    return {
      id: selectedCat._catId, code: selectedCat.code, label: selectedCat.label,
      count: itemCount, counts: selectedCat.counts || {},
      cost_class: selectedCat.cost_class ?? null,
    };
  }, [selectedCat, isL3, lines]);

  // An L3 moves like any other category (handleReparentCategory) — its own id
  // never changes (only its parent_id), so unlike the family move there is no
  // re-navigation needed after.
  const handleCategoryParentMove = async (catId, newL2Id, newL1Node) => {
    await handleReparentCategory(catId, newL2Id, newL1Node);
    setEditCatRowId(null);
  };

  const categoryHeaderCols = useMemo(() => {
    // Parent cell — the breadcrumb's replacement, one control per §5 ("read =
    // navigate, edit = move"):
    //   L1 — no ancestor, nothing to show or move. No column at all.
    //   L2 — one ancestor (L1), but NO move mechanism exists for an L2 (cross-L1
    //        is disallowed by design, and nothing today re-parents an L2 either)
    //        — crumbs only, never editable.
    //   L3 — reuses the SAME move-to-a-different-L2 cascade its own child rows
    //        already offer one level up (CatalogReparent variant='l3').
    let parentCol = null;
    if (selectedLevel === 2) {
      parentCol = {
        key: '_move', label: 'Parent', width: 160,
        render: () => {
          const l1 = selectedAncestors.l1Id != null ? tree.find(n => n._catId === selectedAncestors.l1Id) : null;
          const nodeId = selectedAncestors.l1Id != null ? `c1-${selectedAncestors.l1Id}` : null;
          return (
            <Breadcrumb
              dense
              crumbs={[{ label: l1 ? l1.code : '—', onClick: (handleSelectNode && nodeId != null) ? () => handleSelectNode(nodeId) : undefined }]}
            />
          );
        },
      };
    } else if (selectedLevel === 3) {
      parentCol = {
        key: '_move', label: 'Parent', width: 240,
        render: row => {
          const inEdit = editCatRowId != null && String(row.id) === String(editCatRowId);
          return (
            <CatalogReparent
              tree={tree} variant="l3"
              l1Id={selectedAncestors.l1Id} l2Id={selectedAncestors.l2Id}
              onMoveCategory={(newL2Id, newL1Node) => handleCategoryParentMove(row.id, newL2Id, newL1Node)}
              disabled={!inEdit || !canArrange}
              onNavigate={nodeId => handleSelectNode(nodeId)}
              currentL3={{ id: row.id, code: row.code }}
            />
          );
        },
      };
    }
    // L1's own header: same cost_class chip the root grid shows on L1 rows
    // (Alex, 2026-07-02 — makes the stable engine classification visible now
    // that L1 codes are user-editable).
    const extraCols = selectedLevel === 1 ? [{
      key: 'cost_class', label: 'Cost Class', width: 100, align: 'center',
      render: row => row.cost_class ? (
        <span style={{
          fontFamily: 'var(--ff-mono)', fontSize: 'var(--fs-mono)', fontWeight: 700,
          color: 'var(--th-navy)', background: 'var(--tg-chip-bg)',
          border: '1px solid var(--tg-chip-border)', borderRadius: 4,
          padding: '1px 6px',
        }}>{row.cost_class}</span>
      ) : null,
    }] : [];
    return makeFolderCols({
      seq: false, parentCol, editable: true,
      items: { value: row => row.count },
      statusMode: 'counts', extraCols,
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps — handleCategoryParentMove/
  // handleSelectNode are recreated per render; selectedLevel + edit state drive recompute.
  }, [selectedLevel, selectedAncestors, tree, editCatRowId, canArrange]);

  const handleCategoryHeaderSave = (rowId, field, value) => {
    if (field === 'code') { handleUpdateCategory(rowId, { code: value }); return; }
    if (field !== 'label') return;
    handleUpdateCategory(rowId, { label: value });
  };

  // renderSubRow: ExplainerSubLine under the selected line.
  // RM/HR L1 codes resolved by cost_class — L1 codes are user-editable, so they
  // must never be hardcoded in the chip badges (Q9).
  const rmL1 = tree.find(c => c.cost_class === 'RM')?.code || 'RM';
  const hrL1 = tree.find(c => c.cost_class === 'HR')?.code || 'HR';
  const renderSubRow = isL3 && selectedLineId != null
    ? (row) => {
        const full = lines.find(l => l.id === row.id);
        if (!full) return null;
        return (
          <ExplainerSubLine line={full} rmL1={rmL1} hrL1={hrL1} onNavigate={(pathId) => {
            // pathId is a pipe-separated path string (e.g. "RM|R1|R15" or "HR|H1").
            const [l1, l2, l3] = String(pathId || '').split('|');
            const target = findCatByPath(tree, l1, l2, l3);
            if (target) handleSelectNode(target.id);
          }} />
        );
      }
    : null;

  // Notes (DS-4, Alex 2026-08-21: "use the top right part of the panel to add
  // a card about the notes field") — whichever item is most specific. A
  // selected multi-size family (selectedFamilyId) or the family BEHIND the
  // line you clicked (every line has one — DM-5's "0 nulls of 1283") shows
  // THAT family's notes; otherwise the selected category's. Deliberately
  // never a LINE's own notes — catalog_lines.notes is a different,
  // pre-existing field (service tags / audit trail, STATUS.md) this card
  // must not touch.
  const notesFamilyId = selectedFamilyId ?? (selectedLineId != null
    ? lines.find(l => l.id === selectedLineId)?.family_id ?? null
    : null);
  const notesFamilyNode = useMemo(() => {
    if (notesFamilyId == null || !selectedCat?.children) return null;
    return selectedCat.children.find(c => c.isFamily && c.family_id === notesFamilyId) || null;
  }, [selectedCat, notesFamilyId]);
  const notesTarget = useMemo(() => {
    if (notesFamilyNode) {
      // The RAW label, not the tree leaf's own `label` — that one carries a
      // "(9 sizes)" suffix built for the TREE row, which would double up
      // against Card's own parenthetical here. Same label the grid's family
      // row already shows (both trace to catalog_families.label via the
      // line's own SQL join, DM-5).
      const rawLabel = lines.find(l => l.family_id === notesFamilyId)?.label ?? notesFamilyNode.label;
      return { kind: 'family', id: notesFamilyNode.family_id, notes: notesFamilyNode.notes, label: rawLabel };
    }
    if (selectedCat) return { kind: 'category', id: selectedCat._catId, notes: selectedCat.notes, label: selectedCat.label };
    return null;
  }, [notesFamilyNode, selectedCat, lines, notesFamilyId]);
  const saveNotes = async (text) => {
    if (!notesTarget) return;
    const url = notesTarget.kind === 'family'
      ? `/api/catalog/families/${notesTarget.id}`
      : `/api/catalog/categories/${notesTarget.id}`;
    const res = await fetch(url, {
      method: 'PATCH', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ notes: text }),
    });
    if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.error || 'Save failed'); }
    await refreshTree();
  };
  const rightPanel = (
    <NotesSection
      key={notesTarget ? `${notesTarget.kind}-${notesTarget.id}` : 'none'}
      notes={notesTarget?.notes}
      itemLabel={notesTarget ? notesTarget.label : 'nothing selected'}
      onSave={saveNotes}
      storageKey="catalog:section:notes"
      applicable={notesTarget != null}
      naReason="Select a category or family to see its notes."
    />
  );

  // ⚠ THE ASSISTANT IS THE HOST'S, NOT THIS SCREEN'S, WHEN EMBEDDED. The Onshape screen
  // renders this entire screen inside its OWN HierarchyWorkspace (OnshapeView's `Embedded`),
  // and that workspace already carries an assistant — so passing one here too put two chat
  // panels side by side. Alex, 2026-08-16: "Still two side panels. This should be
  // impossible!" It was possible because each screen owns its own workspace and neither can
  // see the other, which is exactly the seam a shared SLOT does not cover. `hideNav` is
  // already the "I am embedded" flag, so the rule is one line: the host owns the chrome.
  // `screen` scopes the chat's write tool to the shelf open here (L3 only).
  const assistant = (
    <AssistantSection
      screen={isL3 && activeL3Id ? { kind: 'catalog', id: activeL3Id, label: [selectedCat.code, selectedCat.label].filter(Boolean).join(' ') } : null}
    />
  );

  return (
    <HierarchyWorkspace
      rightPanel={rightPanel}
          rightBottom={assistant}
          treeNodes={navTree}
          // Tree highlight = the L3 itself. navTree has no family/line leaves since
          // UX-40, so passing the leaf id (`c3-N|fam-M`, as this did before) matched
          // no node: a family deep link opened with the tree fully collapsed and
          // nothing highlighted (Alex, 2026-10-11). TreeNav expands to `selected`.
          selectedNodeId={selectedNodeId}
          onSelectNode={handleSelectNode}
          treeHeader={<ViewSwitcher view={view} setView={setView} onSelectRoot={() => handleSelectNode(null)} />}
          treeFilterAside={workingIn ? <ShowStandardCheck workingCode={workingCode} checked={showStandard} onChange={setShowStandard} /> : null}
          treeEmptyMessage={projectOnly && !navTree.length ? nothingOwnMessage(workingCode) : undefined}
          filterPlaceholder="Filter activities…"
          hideNav={hideNav}
          storageKey="catalog"
          navWidthStorageKey="inoviu:navWidth"
          mainStyle={{ display: 'flex', flexDirection: 'column', overflow: 'hidden', padding: `${PANE_GAP}px ${PANE_GAP}px 0` }}
          mainHeader={
            <>
              {/* BREADCRUMB RETIRED entirely on this screen (Alex, 2026-07-20:
                  phases 1+2 did leaf pages, phase 3 — "go" — does category
                  levels). Root has no node to show a row for, so it keeps the
                  hint text; every other selection now has a header row of its
                  own (category, family, or line) and the Parent cell carries
                  the path that the breadcrumb used to. */}
              {!selectedNodeId && (
                <Card storageKey="catalog:section:no-selection" title="Category">
                  <span style={{ color: 'var(--th-text-hint)', fontSize: 'var(--fs-body)' }}>Select a node in the navigation tree.</span>
                </Card>
              )}
              {categoryHeaderRow && (
                <HeaderRow
                  title="Category"
                  row={categoryHeaderRow}
                  columns={categoryHeaderCols}
                  onSave={handleCategoryHeaderSave}
                  onEditRow={setEditCatRowId}
                  rowLocked={() => folderLock}
                  editRowId={editCatRowId}
                  storageKey="catalog-category-header"
                  card
                />
              )}
              {/* A category's Onshape ASSEMBLY — below its header row (the row
                  identifies the item, the section details its CAD side). EVERY
                  level (phase 3): assemblies nest, so an L1 or L2 points at the
                  assembly containing its children's. Stays mounted regardless
                  of a family/line selected beneath it — UX-40: the category
                  header row no longer stands down for those (see the comment
                  on categoryHeaderRow). */}
              {categoryHeaderRow && (
                <CategoryOnshapePanel
                  categoryId={categoryHeaderRow.id}
                  onshapeAssemblyRef={selectedCat?.onshape_ref || ''}
                  patchUrl={`/api/catalog/categories/${categoryHeaderRow.id}`}
                  applicable={selectedL1Class !== 'HR'}
                  naReason="Labour has no CAD model — Onshape never applies here."
                  storageKey="catalog-category"
                  onSaved={refreshTree}
                />
              )}
            </>
          }
        >
          {/* UX-40 (Alex, 2026-08-21, on a tall shelf: "I need a vertical scroll
              bar... keep at least the first card frozen"). CATEGORY + its own
              3D MODEL (above, in mainHeader) stay pinned; everything below —
              the family's own Onshape section, the family grid, the Sizes
              card, Used in products/Placed in functions — is what HERE means:
              the shell's own pinned-header scroll region (HierarchyWorkspace,
              opted into by this screen's own mainStyle overflow:'hidden'), not
              a second hand-rolled copy of it. Was hand-rolled here first; the
              shell grew the same wrapper (2026-08-21, "Same for all… Typical")
              so Products/Projects/etc. get it without re-deriving it. */}
            {/* ERR-1 — an action failure appears HERE, directly above the grid it
                came from, instead of in a browser popup that hid the row and then
                threw the message away. Stays until dismissed or superseded.
                The ref scrolls it into view: on a long grid this sits above the
                fold, so a refused move ("that tag is already used here") looked
                exactly like nothing happening. The message existed; it was just
                off-screen. */}
            <div ref={actionErrorRef}>
              {isL3 && (
                <AssistantWriteNotice
                  write={assistantWrite.write}
                  groups={[
                    { title: 'Families', items: assistantWrite.write?.changes?.families || [] },
                    { title: 'Lines (shaded below)', items: assistantWrite.write?.changes?.lines || [] },
                  ]}
                  error={assistantWrite.error}
                  onUndo={assistantWrite.undo}
                  onDismiss={assistantWrite.dismiss}
                />
              )}
              <Notice onDismiss={() => setActionError(null)}>{actionError}</Notice>
              <Notice kind="success" onDismiss={() => setCopyNotice(null)}>{copyNotice}</Notice>
            </div>
            {/* COS-6 — the question is asked HERE, in the app, above the grid it
                concerns, and can offer more than yes/no. */}
            {pendingConfirm && (
              <ConfirmPanel
                title={pendingConfirm.title}
                question={pendingConfirm.question}
                impact={pendingConfirm.impact}
                actions={pendingConfirm.actions}
                onChoose={resolveConfirm}
              />
            )}
            {/* The card (2026-08-20, Alex: "same concept, the data grid should be in
                a card") — titled by what this ONE grid is showing at the moment,
                since it does triple duty: root shows Categories, inside a category
                (not yet L3) shows Sub-categories, and at L3 shows a category's own
                Family rows. Same variables the emptyMessage below already branches
                on, so the two can never disagree about what's on screen. */}
            <Card storageKey="catalog:section:grid" title={gridCardTitle} count={!loading ? gridRows.length : undefined}>
            {/* The one scrollport: BOTH axes, so the sticky header (top: 0) and
                the sticky-left icon block keep a scroll container to stick to.
                Splitting the axes across two elements would silently break both
                -- a box with overflow-x:auto computes overflow-y to auto as well,
                becoming a scrollport that never scrolls, which is exactly where a
                sticky header stops sticking. */}
            {/* `0 1 auto`, not `1` (Alex, 2026-07-27: "the horizontal slider is
                still on the bottom"). Growing to fill the pane put the scrollbar
                at the bottom of the SCREEN -- with 8 rows on the shelf it sat a
                long way under the last row, reading as the page's scrollbar
                rather than the grid's. No-grow makes the box as tall as the grid,
                so the bar sits directly beneath the rows; shrink still caps it at
                the pane on a long shelf, where it scrolls vertically as before. */}
            <div style={{ flex: '0 1 auto', minHeight: 0, overflow: 'auto', paddingBottom: 4 }}>
            <DataGrid
              key={selectedNodeId || 'root'}
              rows={gridRows}
              columns={catalogCols}
              onRowDoubleClick={handleGridRowDoubleClick}
              // The family's 3D Model and Sizes cards follow the row you click,
              // even as that click opens it for edit (Alex, 2026-09-30). No
              // scroll here — that would pull the screen away from the row
              // being edited; the 3D and "N sizes" cells are what scroll.
              onRowSelect={row => { if (row.family_id != null) setSelectedFamilyId(row.family_id); }}
              selectedRowId={selectedRowId}
              loading={loading}
              emptyMessage={isL3 ? 'No families in this category.' : selectedCat ? 'No sub-categories yet. Add one below.' : 'No catalog categories.'}
              onSave={handleSave}
              onAdd={isL3 || canArrange ? gridOnAdd : undefined}
              onCopy={isL3 ? handleCopyLine : undefined}
              onDelete={handleDelete}
              onDeleteCheck={handleDeleteCheck}
              onReorder={canArrange ? handleReorder : undefined}
              rowLocked={isL3 ? (r => lockOwned(r.owner_project_id, r._ownerOnly)) : (() => folderLock)}
              // UX-40 — a family-summary row (n>1) has no single line to save an
              // edit to; editing it lives in the Sizes card. Guard here rather
              // than relying on every column to refuse edit mode on its own.
              // A multi-size family row opens for edit too (note 8) — only its Tag
              // and Label are editable (catalogCols' per-row `editable`), and it
              // edits under its own editAggId so the size-level editors stay shut.
              onEditRow={id => {
                const target = id == null ? null : gridRows.find(r => String(r.id) === String(id));
                if (target?._type === 'familyAgg') { setEditRowId(null); setEditAggId(id); return; }
                setEditAggId(null);
                setEditRowId(id);
              }}
              // Auto-edit (2026-08-22, Alex: "switch everything to auto edit"; 2026-08-23,
              // "one click to edit everywhere and remove the icon" — now unconditional,
              // including L3: opening the Sizes card / ExplainerSubLine moved to
              // double-click, above, so single click is free everywhere.
              editOnRowClick
              deleteImpact={row => row._type === 'familyAgg' ? `This deletes the item and all ${row.sizeCount} of its sizes.` : null}
              editRowId={editAggId ?? editRowId}
              getRowStyle={gridRowStyle}
              renderSubRow={renderSubRow}
              footerRow={catalogFooterRow}
              // UX-7 (Alex, 2026-07-30: "we should freeze all columns up to Size for
              // catalog datagrid"). Derived from where the Size column actually IS, not
              // a hardcoded 5 -- a column reorder would silently freeze the wrong block.
              // Only on the LINE grid: the folder grid has no Size and is narrow enough
              // to need no freezing.
              stickyCols={isL3 ? (catalogCols.findIndex(c => c.key === 'size') + 1) || 1 : 1}
              storageKey={isL3 ? 'catalog-line' : 'catalog-category'}
              multiSelect={isL3}
              /* GRID TOOLBAR, now INSIDE the bulk bar (Alex, 2026-08-19, annotating a
                 screenshot: status chips and Copy shelf into that row, and hold its
                 height). Two rows became one, and because the bar is now always
                 rendered, picking rows no longer inserts anything — the same fix as
                 UX-35, applied to the other thing that moved the grid. */
              toolbar={isL3 ? (
                <>
                  {/* Copy shelf (NIGHT_SHIFT.md item 2) — duplicate this WHOLE L3
                      into a new sibling under the same L2 ("make Mexico from US"). */}
                  <button
                    onClick={handleCopyShelf}
                    title="Duplicate this whole shelf (all lines) into a new sibling category"
                    style={{
                      // padding stays the toolbar's own compact '2px 8px', not
                      // BTN_SECONDARY's — this sits in DataGrid's toolbar row, which
                      // needs to match the bulk-bar chips' height exactly (the
                      // documented row-jitter fix); only radius/color follow the
                      // shared secondary role.
                      display: 'inline-flex', alignItems: 'center', gap: 4, whiteSpace: 'nowrap',
                      background: BTN_SECONDARY.background, color: BTN_SECONDARY.color,
                      border: BTN_SECONDARY.border, borderRadius: BTN_SECONDARY.borderRadius,
                      padding: '2px 8px', cursor: 'pointer', fontSize: 'var(--fs-small)',
                    }}
                  >{COPY} Copy shelf</button>
                  {/* Everything from here is STATE, not action — pushed right. */}
                  <span style={{ marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: 12, flexShrink: 0 }}>
                    <CatalogStatusFilter statusFilter={statusFilter} onToggle={toggleStatus} />
                  {loading && <Text kind="hint" style={{ whiteSpace: 'nowrap' }}>Loading…</Text>}
                  {gridRows.length > 0 && !loading && (
                    <Text kind="note" style={{ whiteSpace: 'nowrap' }}>
                      {gridRows.length} famil{gridRows.length !== 1 ? 'ies' : 'y'}
                      {statusFilter.length < STATUSES.length
                        ? ` (${gridRows.filter(r => !statusFilter.includes(r.status)).length} muted)`
                        : ''}
                    </Text>
                  )}
                  </span>
                </>
              ) : null}
            />
            </div>
            </Card>
            {/* Family-level Onshape linking (2026-07-03) — the config-base /
                size-param editors, the one thing about a selected family the
                grid row itself doesn't carry (Tag/Label/Status/Items do, which
                is why the old wide "Family" row was retired — pure duplicate).
                BELOW the grid with the family's other cards (Alex, 2026-09-30:
                "it will be better after the family grid, before Sizes and
                Where used") — above it, it sat with the CATEGORY's 3D Model
                card and read as part of the shelf, not the family you picked. */}
            <div ref={modelCardRef} />
            {selectedFamilyNode && (
              <FamilyOnshapePanel
                familyId={selectedFamilyNode.family_id}
                onshapeRef={selectedFamilyNode.onshape_ref}
                onshapeConfig={selectedFamilyNode.onshape_config}
                onshapeSize={selectedFamilyNode.onshape_size}
                onSaved={refreshTree}
                costClass={selectedL1Class}
              />
            )}
            {/* UX-40(a)+(b) — the Sizes card: a multi-size family's own size
                ladder, using the exact same per-line columns/editors the top
                grid used to show for every family before this change.
                ALWAYS mounted once at L3 (Alex, 2026-08-21, on it being
                missing with nothing selected: it should read the same as
                Used in products/Placed in functions below — present, just
                saying it has nothing to show yet, not absent). Not-applicable
                covers TWO cases in the SAME not-applicable state as the 3D
                Model section (UI_VISION §4's two-axis contract, Card's own
                applicable/naReason): greyed title, closed non-interactive
                chevron, no body — nothing selected, or a single-size family
                (its price/hrs are already the family row's own, edited inline
                up there). Only a real multi-size family opens it, populated. */}
            <div ref={sizesCardRef} />
            {isL3 && (
              <Card
                storageKey="catalog:section:sizes" title="Sizes"
                count={selectedFamilyId == null ? 'nothing selected' : sizeCardRows.length}
                // A ONE-size item on a real ladder opens too (Alex, 2026-10-11: "it's for
                // everything that has sizes"). Closed, its only route to a second size was
                // the top grid's + Add and Copy — and both make a NEW item. Only an item
                // with no ladder yet ('unique') stays closed: it gets one on the row above.
                applicable={selectedFamilyId != null
                  && ((familyLineCount[selectedFamilyId] || 0) > 1 || !!familySizeListById[selectedFamilyId])}
                naReason={selectedFamilyId == null
                  ? 'Select a family to see its sizes.'
                  : 'No size list yet — pick a Size on the family row above, then add sizes here.'}
                open={sizesOpen}
                onToggle={() => setSizesOpen(o => !o)}
              >
                <div style={{ flex: '0 1 auto', minHeight: 0, overflow: 'auto', paddingBottom: 4 }}>
                  <DataGrid
                    key={`sizes-${selectedFamilyId}`}
                    rows={sizeCardRows}
                    columns={catalogCols}
                    onRowDoubleClick={handleGridRowDoubleClick}
                    selectedRowId={selectedRowId}
                    onSave={handleSave}
                    onAdd={fields => handleAddLine(fields, selectedFamilyId)}
                    addFormLabel="Add size"
                    editWarnings={sizeEditWarnings}
                    onCopy={handleCopySize}
                    onDelete={handleDelete}
                    onDeleteCheck={handleDeleteCheck}
                    onReorder={canArrange ? handleReorderSize : undefined}
                    rowLocked={r => lockOwned(r.owner_project_id, r._ownerOnly)}
                    onEditRow={setEditRowId}
                    editRowId={editRowId}
                    editOnRowClick
                    getRowStyle={gridRowStyle}
                    renderSubRow={renderSubRow}
                    stickyCols={(catalogCols.findIndex(c => c.key === 'size') + 1) || 1}
                    storageKey="catalog-line-sizes"
                    multiSelect
                  />
                </div>
              </Card>
            )}
            {/* B1 — docked "Used in products" table under the line grid (isL3 only).
                OUTSIDE the grid's scrollport, in one of its own: it is a different
                table with different columns, so sharing a scrollbar dragged it
                sideways whenever the grid was scrolled, and its height pushed the
                grid's own bar 77px below the last row it belongs to. */}
            {isL3 && (
              <div style={{ flex: '0 1 auto', minHeight: 0, overflow: 'auto', paddingBottom: 24 }}>
                <CatalogWhereUsedTable
                  lineId={selectedLineId}
                  onNavigateProduct={onNavigateToProduct}
                  onNavigateToPlacement={onNavigateToPlacement}
                />
                {authGroup === 'internal' && <CatalogHistoryCard lineId={selectedLineId} familyId={notesFamilyId} />}
              </div>
            )}
    </HierarchyWorkspace>
  );
}

// Locate the L3 category node matching an l1/l2/l3 path by code, for cross-view nav.
function findCatByPath(tree, l1, l2, l3) {
  const l1Node = (tree || []).find(n => n.code === l1);
  if (!l1Node || !l2) return l1Node || null;
  const l2Node = (l1Node.children || []).find(n => !n.isLine && n.code === l2);
  if (!l2Node || !l3) return l2Node || null;
  return (l2Node.children || []).find(n => !n.isLine && n.code === l3) || l2Node;
}
